import type {
  DiagnosticSections,
  DiagnosticSnapshot,
  DiagnosticSnapshots,
  SectionEnvelope,
} from "@/api/diagnostic";
import { formatCount } from "@/lib/format";
import { NO_VALUE, sectionCounter } from "./sectionState";

/**
 * Leitura tipada do `data` das seções — funções **puras**, sem DOM e sem i18n.
 *
 * **Narrowing, não segundo contrato.** `SectionEnvelope.data` é
 * `{[key: string]: unknown}` de propósito: engessá-lo no OpenAPI duplicaria a
 * allowlist que já vive no `diagnostic_builder`. A tentação aqui seria declarar
 * uma interface por seção espelhando esse `data` — o que recriaria exatamente o
 * problema que o S-059 resolveu para o envelope, um contrato paralelo que diverge
 * em silêncio. Então nada aqui declara a forma do backend: tudo lê `unknown` e
 * estreita, e o que não casa vira `undefined`, nunca `0`.
 *
 * Três invariantes que os testes travam:
 *
 * 1. **`undefined` jamais vira `0`.** Todo número exibível passa por
 *    `sectionCounter`, que rende `—` em seção fechada. Zero é uma afirmação sobre
 *    o sistema de origem, e só `empty` pode fazê-la.
 * 2. **`_UNKNOWN` é bucket legítimo.** O builder o emite de propósito em qualquer
 *    `by_*` (gap sem `coverage_type`, item sem `lob`, veredito fora do CHECK);
 *    descartá-lo faria o agregado deixar de fechar contra o total declarado.
 * 3. **Agregado vem de agregado.** As listas que trafegam são amostras
 *    (`coverage_gaps` capada em 100, `items` em 200): nenhum total sai delas.
 */

// --------------------------------------------------------------------------
// acesso tolerante — o espelho do `dig`/`_as_dict` do builder
// --------------------------------------------------------------------------

function envelopeOf(
  sections: DiagnosticSections,
  key: string,
): SectionEnvelope | undefined {
  return (sections as Record<string, SectionEnvelope | undefined>)[key];
}

function dataOf(
  sections: DiagnosticSections,
  key: string,
): Record<string, unknown> {
  return rec(envelopeOf(sections, key)?.data);
}

export function rec(value: unknown): Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** `boolean` é `number` em JS coerção — aqui não é. `NaN` também não passa. */
export function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * String **exibível**: em branco vira `undefined`.
 *
 * O SAP preenche campo vazio com espaços, e `""` numa célula é indistinguível de
 * dado — o leitor não sabe se a cidade está vazia ou se a coluna existe. Quem
 * decide o texto de ausência é a aba, com i18n.
 */
function text(value: unknown): string | undefined {
  const raw = str(value)?.trim();
  return raw != null && raw.length > 0 ? raw : undefined;
}

/**
 * `boolean` **de verdade**, ou `undefined`.
 *
 * `activated` é tri-estado no banco (o CHECK da `project_scope_activation` deixa
 * `NULL` para `needs_decision`/`needs_review`), então coagir com `!!` faria
 * "ainda não se sabe" virar "não". A regra do `—` aplicada a booleano.
 */
function bool(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

/**
 * Lista de strings exibíveis. Item que não é string cai fora; a lista fica.
 *
 * Nunca `undefined`: `[]` é o dado quando a origem não tem nota SAP nem tabela
 * disparada, e obrigar o consumidor a checar ausência convidaria ao `?? []`
 * espalhado — que é onde ausência e vazio se confundem.
 */
function strings(value: unknown): string[] {
  return list(value).filter((item): item is string => typeof item === "string");
}

/**
 * Código de licença exibível, como string — aceita número **ou** string.
 *
 * A run real usa `license_type_code` numérico (5, 1, 8 — vocabulário do USMM),
 * mas o payload não garante o tipo. `str()` rejeitaria o número e faria o código
 * desaparecer só porque não é string; aqui o código sobrevive aos dois tipos, e
 * `String(value)` casa byte a byte com o `str(code)` que o backend já aplica para
 * montar a chave de `license_type_distribution`.
 */
function code(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

/** Mapa `chave → contagem`, descartando o que não for número. */
export function countMap(value: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, raw] of Object.entries(rec(value))) {
    const n = num(raw);
    if (n != null) out[key] = n;
  }
  return out;
}

// --------------------------------------------------------------------------
// número exibível
// --------------------------------------------------------------------------

export interface Metric {
  /** Número conhecido, ou `undefined`. Nunca `0` como fallback. */
  value: number | undefined;
  /** Já passado por `sectionCounter`: `—` quando desconhecido. */
  text: string;
}

export type NumberFormat = (n: number) => string;

function metric(
  envelope: SectionEnvelope | undefined,
  value: number | undefined,
  format: NumberFormat,
): Metric {
  if (!envelope) return { value: undefined, text: NO_VALUE };
  return { value, text: sectionCounter(envelope, value, format) };
}

/**
 * A seção não sabe os seus números (`not_collected` / `error` / `unauthorized`).
 *
 * A regra mora em `sectionCounter`, do DIAG.2 — perguntar a ele, em vez de
 * repetir a lista de estados aqui, é o que impede duas definições de "fechado".
 */
function isUnknown(envelope: SectionEnvelope | undefined): boolean {
  return envelope == null || sectionCounter(envelope, 1) === NO_VALUE;
}

/**
 * Desambigua uma lista vazia (`coverage_gaps`/`items`) entre "aprovado e sem
 * gap nenhum" e "ainda sem aprovação de escopo" — as duas produzem `[]`, e só
 * o backend sabe qual das duas é (`BuildContext.scope_approval_active_ids`).
 *
 * Default `true` (assume aprovação) quando o campo não existe: snapshots
 * anteriores a esta correção não o têm, e o comportamento antigo — mostrar a
 * mensagem de "coberto pelo padrão" — é o que já estava lá, não uma mensagem
 * nova para dado velho.
 */
export function hasActiveScopeApproval(
  sections: DiagnosticSections,
  key: "scope_gaps" | "scope_activation",
): boolean {
  return bool(dataOf(sections, key).has_active_approval) ?? true;
}

// --------------------------------------------------------------------------
// aderência ao padrão
// --------------------------------------------------------------------------

/** A fatia sem custo: requisito atendido pelo padrão, sem gap nenhum. */
export const STANDARD_SLICE_KEY = "standard";

// --------------------------------------------------------------------------
// aderência por linha de negócio
// --------------------------------------------------------------------------

export interface LobRow {
  lob: string;
  total: number;
  activate: number;
  needs_decision: number;
  needs_review: number;
  skip: number;
}

/**
 * Uma linha por LoB, ordenada por **decisões pendentes desc** — é a pergunta que
 * a barra responde ("onde o analista ainda tem trabalho").
 *
 * Desempate por total desc e depois por nome: empate em `needs_decision` é comum
 * e, sem desempate estável, a barra trocaria de ordem entre dois refreshes.
 */
export function lobRows(sections: DiagnosticSections): LobRow[] {
  if (isUnknown(envelopeOf(sections, "scope_activation"))) return [];

  const byLob = rec(dataOf(sections, "scope_activation").by_lob);
  return Object.entries(byLob)
    .map(([lob, raw]) => {
      const counts = countMap(raw);
      return {
        lob,
        total: counts.total ?? 0,
        activate: counts.activate ?? 0,
        needs_decision: counts.needs_decision ?? 0,
        needs_review: counts.needs_review ?? 0,
        skip: counts.skip ?? 0,
      };
    })
    .sort(
      (a, b) =>
        b.needs_decision - a.needs_decision ||
        b.total - a.total ||
        a.lob.localeCompare(b.lob),
    );
}

// --------------------------------------------------------------------------
// catálogo WRICEF por letra
// --------------------------------------------------------------------------

/** Ordem do acrônimo, não alfabética — é como o cliente lê a taxonomia. */
/**
 * O sentinela de "coluna nula no dado", espelhando o `UNKNOWN` do
 * `wricef_catalog_stats`. Não é lixo a descartar: o agregado deixa de fechar
 * contra o total declarado se ele sumir (regra 2 do cabeçalho deste módulo).
 */
const UNKNOWN_BUCKET = "_UNKNOWN";

const WRICEF_ORDER = ["W", "R", "I", "C", "E", "F"] as const;

/**
 * Disposição da pior para a melhor. A coluna do design empilha com o pior no
 * topo, então esta é a ordem do array.
 *
 * `undecided` lidera por ser a que **não se sabe** — a que ainda vai custar
 * análise. `retire` fica abaixo de `requires_extension` porque objeto aposentado
 * não gera trabalho de extensão.
 *
 * ⚠️ Estes são os quatro tokens reais de `disposition`. Não existe "migrar como
 * está" nesta coluna: o token de "sem alteração" é `conversion_tipo`, outra
 * coluna, que não trafega no payload.
 */
const DISPOSITION_ORDER = [
  "undecided",
  "requires_extension",
  "retire",
  "standard_grow_equivalent",
] as const;

export interface WricefSegment {
  disposition: string;
  value: number;
}

export interface WricefColumn {
  letter: string;
  total: number;
  segments: WricefSegment[];
}

/**
 * Uma coluna por letra, cruzando `by_wricef` (a marginal) com
 * `by_wricef_disposition` (a tabela cruzada).
 *
 * A tabela cruzada **já vem pronta** do `assemble_stats`; recalculá-la de
 * `by_wricef` + `by_disposition` daria outro número, porque as duas marginais não
 * determinam o cruzamento.
 */
export function wricefLetterColumns(
  sections: DiagnosticSections,
): WricefColumn[] {
  if (isUnknown(envelopeOf(sections, "wricef_catalog"))) return [];

  const data = dataOf(sections, "wricef_catalog");
  const byLetter = countMap(data.by_wricef);
  const cross = rec(data.by_wricef_disposition);

  const known = WRICEF_ORDER.filter((letter) => letter in byLetter) as string[];
  const extra = Object.keys(byLetter)
    .filter((letter) => !(WRICEF_ORDER as readonly string[]).includes(letter))
    .sort();

  return [...known, ...extra].map((letter) => {
    const buckets = countMap(cross[letter]);
    const declared = DISPOSITION_ORDER.filter((token) => token in buckets).map(
      (token) => ({
        disposition: token as string,
        value: buckets[token],
      }),
    );
    const unknown = Object.keys(buckets)
      .filter(
        (token) => !(DISPOSITION_ORDER as readonly string[]).includes(token),
      )
      .sort()
      .map((token) => ({ disposition: token, value: buckets[token] }));
    return {
      letter,
      total: byLetter[letter],
      segments: [...declared, ...unknown],
    };
  });
}

/**
 * Alvo de extensibilidade, do mais limpo para o menos limpo.
 *
 * A ordem é a do Clean Core, e é ela que a barra empilha: `key_user` não escreve
 * ABAP, `side_by_side` sai da stack, `developer` é ABAP on-stack em API liberada,
 * e `classic` é a extensão on-stack clássica (BAdI não liberado, user exit,
 * modificação), a menos limpa das quatro.
 *
 * ⚠️ São **quatro**, e a lista não é fechada. A `ref_clean_core_decisions` só
 * conhece três porque o `CHECK` dela rejeita `classic`, mas quem produz o dado é
 * o Architect (`_ON_STACK_DOMAIN_TO_TARGET`), que emite os quatro, e a
 * `project_wricef_catalog` não tem `CHECK` nenhum. Alvo desconhecido entra pelo
 * mesmo caminho que letra WRICEF desconhecida em `wricefLetterColumns`: no fim,
 * ordenado, nunca descartado.
 */
const TARGET_ORDER = [
  "key_user",
  "side_by_side",
  "developer",
  "classic",
] as const;

export interface ExtensionTargetSlice {
  target: string;
  value: number;
}

export interface ExtensionTargetBreakdown {
  /** O balde `requires_extension` inteiro. As fatias somam exatamente isto. */
  total: number;
  slices: ExtensionTargetSlice[];
  /**
   * Objetos com alvo gravado **fora** do balde de extensão. Não entram nas
   * fatias, porque somá-los diria que são trabalho de extensão, e não são.
   *
   * Existem porque o `CHECK` que amarra alvo a `requires_extension` está na
   * `ref_clean_core_decisions`, não na `project_wricef_catalog`. No dado local
   * de 12/09/2026 são 4 objetos `undecided` com alvo `developer`.
   */
  outsideExtension: number;
}

/**
 * O balde `requires_extension` subdividido por alvo (#687).
 *
 * Lê o **cruzamento** `by_disposition_target`, nunca a marginal
 * `by_extensibility_target`. A marginal agrega o catálogo inteiro: medida em
 * 12/09/2026 ela diria `developer: 47` com 4 desses objetos fora do balde, e
 * `_UNKNOWN: 456` misturando 35 objetos de extensão sem alvo com 421 que nem
 * pertencem a ele. É o mesmo motivo pelo qual `wricefLetterColumns` não
 * recalcula o cruzamento a partir das duas marginais.
 *
 * O `_UNKNOWN` fica **dentro** das fatias de propósito: é objeto que exige
 * extensão e ainda não tem alvo decidido, e é ele que faz a soma fechar com o
 * balde. Escondê-lo daria uma barra que não bate com o próprio total.
 *
 * #1343 F0: quando a origem já traz `by_disposition_target_lettered`, ele
 * vence. É o mesmo cruzamento, repetido só com quem tem letra WRICEF — as
 * barras de cima do card leem só letra, e este bloco precisa fechar com elas,
 * não com o catálogo inteiro (que soma 71 contra 59 na AL5). `!= null`, e não
 * `in`: o builder grava `null` quando a origem não tem a chave, e tanto a
 * chave ausente quanto `null` caem no cruzamento antigo — é o caso de um
 * snapshot publicado pela Calibragem antes desta mudança.
 */
export function extensionTargets(
  sections: DiagnosticSections,
): ExtensionTargetBreakdown | undefined {
  if (isUnknown(envelopeOf(sections, "wricef_catalog"))) return undefined;

  const data = dataOf(sections, "wricef_catalog");
  const cross = rec(
    data.by_disposition_target_lettered != null
      ? data.by_disposition_target_lettered
      : data.by_disposition_target,
  );
  const buckets = countMap(cross.requires_extension);
  if (Object.keys(buckets).length === 0) return undefined;

  const declared = TARGET_ORDER.filter((token) => token in buckets).map(
    (token) => ({
      target: token as string,
      value: buckets[token],
    }),
  );
  const extra = Object.keys(buckets)
    .filter((token) => token !== UNKNOWN_BUCKET)
    .filter((token) => !(TARGET_ORDER as readonly string[]).includes(token))
    .sort()
    .map((token) => ({ target: token, value: buckets[token] }));
  const unknown =
    UNKNOWN_BUCKET in buckets
      ? [{ target: UNKNOWN_BUCKET, value: buckets[UNKNOWN_BUCKET] }]
      : [];

  const slices = [...declared, ...extra, ...unknown];
  const outsideExtension = Object.entries(cross)
    .filter(([disposition]) => disposition !== "requires_extension")
    .reduce((acc, [, targets]) => {
      const counts = countMap(targets);
      return (
        acc +
        Object.entries(counts)
          .filter(([token]) => token !== UNKNOWN_BUCKET)
          .reduce((sum, [, value]) => sum + value, 0)
      );
    }, 0);

  return {
    total: slices.reduce((acc, slice) => acc + slice.value, 0),
    slices,
    outsideExtension,
  };
}

/** Uma família de documento e o fluxo dela (#684). */
export interface DocumentFamilyVolume {
  family: string;
  table: string;
  /** `line_item` ou `document`, conforme o contrato de Digital Access conta. */
  unit: string;
  /** `fiscal_year` ou `created_on`. Só `BSEG` e `MSEG` têm ano fiscal. */
  axis: string;
  collected: boolean;
  total: number | undefined;
  byYear: Record<string, number>;
  byMonth: Record<string, number>;
  /**
   * `false` quando a família não tem campo de mês nenhum.
   *
   * Não é o mesmo que série mensal zerada: doze zeros numa família sem eixo se
   * leriam como ausência de documento, e é ausência de **campo**.
   */
  monthlySupported: boolean;
  /**
   * Por que a família não trouxe número.
   *
   * Só existe em família não coletada. Sem ele, "a origem recusou a contagem"
   * e "não há documento no período" ficam com a mesma cara na tela: a
   * primeira é o que aconteceu num S/4HANA de cliente em 02/10/2026, com as
   * nove famílias em `TABLE_NOT_FOUND`.
   */
  reason?: string;
  /**
   * Linhas da **tabela inteira**, quando foi só o que a origem conseguiu dar.
   *
   * Vem do `EM_GET_NUMBER_OF_ENTRIES`, que não aceita WHERE: não há recorte de
   * período nem de tipo de documento. É resposta para outra pergunta, e por
   * isso chega fora de `total` e sem ligar `collected`.
   *
   * A tela tem de etiquetá-lo. Numa coluna cujo vizinho é fluxo por ano, um
   * acumulado sem etiqueta é lido como fluxo.
   */
  tableTotal?: number;
  /**
   * `true` quando a contagem bateu o teto de linha ou o orçamento de tempo.
   *
   * O número vira **piso**, não total. Apresentá-lo como total afirmaria o que
   * não foi medido, que é o oposto do que o próprio payload declara.
   */
  totalIsFloor: boolean;
}

export interface DocumentVolumes {
  years: string[];
  families: DocumentFamilyVolume[];
  /** Quantas das nove responderam. Zero é "não coletado", não "volume zero". */
  collectedFamilies: number;
  /** Anos que a janela do projeto pediu. */
  requestedYears?: number;
  /** Só existe quando o pedido não coube no teto do connector. */
  cappedYears?: number;
}

/**
 * Fluxo de documento por período (#684).
 *
 * O `data_volumes` responde outra pergunta: conta linha de tabela, que é
 * estoque. Aqui é criação por período, que é o que o Ato 1 mostra e o que o
 * Digital Access cobra, porque a métrica do contrato é anual.
 */
export function documentVolumes(
  sections: DiagnosticSections,
): DocumentVolumes | undefined {
  if (isUnknown(envelopeOf(sections, "document_volumes"))) return undefined;

  const data = dataOf(sections, "document_volumes");
  const families = (Array.isArray(data.families) ? data.families : []).map(
    (raw) => {
      const item = (raw ?? {}) as Record<string, unknown>;
      return {
        family: str(item.family) ?? "",
        table: str(item.table) ?? "",
        unit: str(item.unit) ?? "",
        axis: str(item.axis) ?? "",
        collected: item.collected === true,
        total: num(item.total) ?? undefined,
        byYear: countMap(item.by_year),
        byMonth: countMap(item.by_month),
        monthlySupported: item.monthly_supported === true,
        reason: text(item.reason),
        tableTotal: num(item.table_total) ?? undefined,
        totalIsFloor: item.total_is_floor === true,
      };
    },
  );

  return {
    years: (Array.isArray(data.years) ? data.years : []).map((y) => String(y)),
    families,
    collectedFamilies: num(data.collected_families) ?? 0,
    requestedYears: num(data.requested_years) ?? undefined,
    cappedYears: num(data.capped_years) ?? undefined,
  };
}

export interface CodeVitality {
  total: number;
  /** O que a fonte alcançou. É este o denominador do percentual. */
  measuredTotal: number;
  executed: number;
  dormant: number;
  unmeasured: number;
  /** `undefined` quando não houve medição. Nunca `0`, nunca `100`. */
  dormantPct: number | undefined;
  source: string | undefined;
  /**
   * A fonte mediu, ou só foi carimbada? (#649)
   *
   * O connector escreve `usage_data_source` assim que a chamada volta, antes de
   * olhar se veio linha, então o nome sozinho não responde. `undefined` é
   * snapshot anterior ao campo, e não é o mesmo que `false`.
   */
  usageMeasured: boolean | undefined;
  /**
   * Quantos objetos cada fonte provou ter executado (#1113).
   *
   * A camada 1 tem de dizer **qual** fonte sustentou a afirmação: "41
   * execuções agendadas" e "visto pelo ST03N" não valem o mesmo numa conversa
   * de escopo. Vazio em snapshot anterior à issue.
   */
  executedBySource: Record<string, number>;
  /** Dos não medidos, quantos têm porta de entrada. Possibilidade, não execução. */
  unmeasuredReachable: number | undefined;
  /** Os sem porta nenhuma. O candidato a descarte mora aqui, e só aqui. */
  unmeasuredWithoutEntry: number | undefined;
  /** Quantos objetos do inventário podem rodar (PROG/FUGR/CLAS/LDBA). */
  executableTotal: number | undefined;
  /** O sistema tem histórico de job? `false` é ausência de fonte, não veredito. */
  jobCollected: boolean | undefined;
  windowMonths: number | undefined;
  /**
   * A coleta atende o "at least one year" do Custom Code Migration Guide? (#864)
   * Só existe com o ABAP Call Monitor; `undefined` com as outras fontes.
   */
  meetsSapMinimum: boolean | undefined;
  /**
   * Quantos dias a leitura de fato cobriu (#646).
   *
   * O agregador do SWNC não aceita intervalo, e a janela **pedida** nunca foi a
   * **lida**: medido em 16/09/2026, pedido de 144 meses e cobertura de 3 dias.
   * `windowMonths` fica `undefined` abaixo de um mês, e aí este é o único
   * número que a tela pode dizer sem mentir.
   */
  coveredDays: number | undefined;
}

/**
 * Vitalidade do código próprio (#752), o dado do Ato 1.
 *
 * `dormantPct` ausente é a afirmação certa quando não se mediu. Medido no banco
 * em 12/09/2026: uma run trouxe 32.894 objetos com `execution_count = 0` porque
 * a cascata de uso parou no `AGR_TCODES`, que é autorização e não mede
 * execução. Ler aquele zero como dormência diria "100% do seu código está
 * morto" na primeira tela que o cliente vê.
 */
export function codeVitality(
  sections: DiagnosticSections,
): CodeVitality | undefined {
  if (isUnknown(envelopeOf(sections, "code_vitality"))) return undefined;

  const data = dataOf(sections, "code_vitality");
  const total = num(data.total);
  if (total == null) return undefined;

  const states = countMap(data.by_state);
  return {
    total,
    measuredTotal: num(data.measured_total) ?? 0,
    executed: states.executed ?? 0,
    dormant: states.dormant ?? 0,
    unmeasured: states.unmeasured ?? 0,
    dormantPct: num(data.dormant_pct) ?? undefined,
    source: text(data.usage_source) ?? undefined,
    usageMeasured:
      typeof data.usage_measured === "boolean"
        ? data.usage_measured
        : undefined,
    executedBySource: countMap(data.executed_by_source),
    unmeasuredReachable: num(data.unmeasured_reachable) ?? undefined,
    unmeasuredWithoutEntry: num(data.unmeasured_without_entry) ?? undefined,
    executableTotal: num(data.executable_total) ?? undefined,
    jobCollected:
      typeof data.job_collected === "boolean" ? data.job_collected : undefined,
    windowMonths: num(data.window_months) ?? undefined,
    meetsSapMinimum:
      typeof data.meets_sap_minimum === "boolean"
        ? data.meets_sap_minimum
        : undefined,
    coveredDays: num(data.covered_days) ?? undefined,
  };
}

// --------------------------------------------------------------------------
// usuários e atividade
// --------------------------------------------------------------------------

export interface ActivityBucket {
  bucket: string;
  count: number;
}

export interface ActivityReason {
  reason: string;
  count: number;
}

export interface ActivitySummary {
  /** Os cinco baldes **na ordem da origem** — o agente já os entrega ordenados. */
  buckets: ActivityBucket[];
  excludedByReason: ActivityReason[];
  totalUsers: number | undefined;
  licensable: number | undefined;
  excluded: number | undefined;
}

/**
 * A régua de atividade, já agregada na origem (`classification_summary.user_activity`).
 *
 * ⚠️ O bloco viaja dentro de `license_measurement`, que é
 * `EMPTY_IS_NOT_COLLECTED`: numa run sem medição de licença a seção fecha e leva
 * `activity` junto, mesmo que a classificação tenha rodado. O card fecha com ela
 * — é a colocação que o backend escolheu, e a alternativa (seção própria) é
 * mudança de contrato.
 *
 * Balde com `count: 0` **permanece**: "nenhum usuário diário" é medição, e a
 * barra empilhada precisa dele para o leitor de tela.
 */
export function activityBar(
  sections: DiagnosticSections,
): ActivitySummary | undefined {
  if (isUnknown(envelopeOf(sections, "license_measurement"))) return undefined;

  const raw = dataOf(sections, "license_measurement").activity;
  if (raw == null || typeof raw !== "object" || Array.isArray(raw))
    return undefined;
  const activity = rec(raw);

  return {
    buckets: list(activity.by_bucket).flatMap((row) => {
      const entry = rec(row);
      const bucket = str(entry.bucket);
      const count = num(entry.count);
      return bucket != null && count != null ? [{ bucket, count }] : [];
    }),
    excludedByReason: list(activity.excluded_by_reason).flatMap((row) => {
      const entry = rec(row);
      const reason = str(entry.reason);
      const count = num(entry.count);
      return reason != null && count != null ? [{ reason, count }] : [];
    }),
    totalUsers: num(activity.total_users),
    licensable: num(activity.licensable),
    excluded: num(activity.excluded),
  };
}

export interface AnalysisWindowInfo {
  months: number;
  start: string | undefined;
  end: string | undefined;
  /** Extremo final tirado de `created_at` porque a run não tem `completed_at`. */
  approximate: boolean;
}

/**
 * A janela que os baldes de uso mediram — `snapshots.functional.analysis_window`.
 *
 * **Fonte única.** `user_activity.analysis_period_months` viaja no bloco do
 * agente e é descartado de propósito pela allowlist do builder. As duas
 * concordam por construção — mesma `input_config`, mesmo mês de 30 dias —, e
 * ter uma só é o que impede que divirjam em silêncio.
 *
 * Qualifica **apenas** os baldes. `licensable`/`excluded` saem de bloqueio,
 * validade da conta e autorização, e nenhum dos três olha para `last_logon`:
 * exibir a janela sobre eles afirmaria um recorte temporal que eles não têm.
 *
 * Sem `months` devolve `undefined`, nunca `0` — janela de zero mês diria que
 * nada foi analisado, que é afirmação diferente de "não sabemos qual foi".
 */
export function analysisWindow(
  snapshots: DiagnosticSnapshots | undefined,
): AnalysisWindowInfo | undefined {
  const raw = rec(rec(rec(snapshots).functional).analysis_window);
  const months = num(raw.months);
  if (months == null) return undefined;

  return {
    months,
    start: str(raw.start),
    end: str(raw.end),
    approximate: raw.approximate === true,
  };
}

// --------------------------------------------------------------------------
// estrutura organizacional — status por entidade
// --------------------------------------------------------------------------

/**
 * As **7 entidades do payload**, na ordem em que o card as mostra.
 *
 * Não são as 7 do design: ele pede Companies · Plants · *Storage loc.* · Sales
 * orgs · Profit centres · *Cost centres* · *Charts of accounts*, e três dessas
 * não existem no `/rfc/org-structure` (T001L não é extraída em lugar nenhum;
 * T004/T004T vão para `erp_config`, fora da allowlist do diagnóstico). Vale o
 * payload — é o que existe.
 *
 * A ordem preserva a relativa do design onde ambos coincidem (empresas → plantas
 * → org. de vendas → centros de lucro) e fecha com as três que o `SPRAS = 'E'`
 * hardcoded esvazia em qualquer sistema PT-BR, para que a ressalva do card se leia
 * como um bloco.
 */
export const ORG_ENTITY_ORDER = [
  "company_codes",
  "plants",
  "sales_orgs",
  "purchasing_orgs",
  "profit_centers",
  "business_areas",
  "divisions",
] as const;

/**
 * Vocabulário de **sub-extração** do `entity_status`.
 *
 * ⚠️ Não é `SectionState` (`ok|partial|empty|not_collected|truncated|unauthorized|
 * error`). São cinco tokens, e os dois nomes que coincidem coincidem por
 * significarem a mesma coisa. Quem traduz cada um é `makeOrgEntityStateMeta`, que
 * é um mapa separado de `makeSectionStateMeta` justamente por isso.
 */
export type OrgEntityState =
  "collected" | "empty" | "error" | "not_collected" | "unrecognized";

const ORG_ENTITY_STATES: ReadonlySet<string> = new Set<string>([
  "collected",
  "empty",
  "error",
  "not_collected",
  "unrecognized",
]);

export interface OrgEntityCounter {
  key: string;
  metric: Metric;
  state: OrgEntityState;
  /** `error_message` do SAP, verbatim. Só existe em `error`. */
  message: string | undefined;
  /** A contagem alcançou o teto de leitura do RFC: o número é piso. */
  capped: boolean;
}

/**
 * Token da entidade, ou `not_collected`.
 *
 * Sem entrada de status ninguém disse nada, e ausência de status é ausência de
 * informação — não sucesso. Token que não está no vocabulário vira
 * `unrecognized`: o backend já traduz o `status` cru, então um valor novo aqui
 * significa que as duas pontas divergiram, e "não sei ler" nunca pode virar OK.
 */
function orgEntityState(raw: unknown): OrgEntityState {
  const token = str(rec(raw).state);
  if (token == null) return "not_collected";
  return ORG_ENTITY_STATES.has(token)
    ? (token as OrgEntityState)
    : "unrecognized";
}

/**
 * Os 7 contadores da aba, cada um qualificado pelo estado da **sua** entidade.
 *
 * É o que impede o pior modo de falha da Feature: `counts[entidade]` é
 * `len(lista)` no backend, e a lista de uma entidade que falhou está vazia — então
 * a entidade em erro chega como `0`. Zero que não quebra, parece dado e é falso.
 *
 * `empty → 0` não é exceção à regra do `—`, é a regra: "a origem respondeu sem
 * registros" é um zero **conhecido**, exatamente como `sectionCounter` já trata
 * uma seção `empty`. Aqui a mesma distinção só desce um nível.
 */
export function orgEntityCounters(
  sections: DiagnosticSections,
  format: NumberFormat = String,
): OrgEntityCounter[] {
  const org = envelopeOf(sections, "org_structure");
  const data = dataOf(sections, "org_structure");
  const counts = countMap(data.counts);
  const statuses = rec(data.entity_status);
  const capped = new Set(
    list(data.capped_entities).flatMap((entity) => {
      const name = str(entity);
      return name != null ? [name] : [];
    }),
  );

  return ORG_ENTITY_ORDER.map((key) => {
    const entry = statuses[key];
    const state = orgEntityState(entry);
    // Só estes dois estados afirmam algo sobre a origem. Os outros três dizem que
    // não se sabe — e `undefined` é o que faz o `sectionCounter` render `—`.
    const known = state === "collected" || state === "empty";
    return {
      key,
      metric: metric(org, known ? counts[key] : undefined, format),
      state,
      message: state === "error" ? text(rec(entry).message) : undefined,
      capped: capped.has(key),
    };
  });
}

export interface CompanyRow {
  comp_code: string | undefined;
  comp_name: string | undefined;
  city: string | undefined;
  country: string | undefined;
  currency: string | undefined;
}

/**
 * As empresas da amostra, com as cinco colunas que existem no payload.
 *
 * Sem "Activity"/"Scope"/"Particularity" do design: nenhuma das três existe em
 * `T001`, e inventar coluna é o mesmo erro que inventar zero.
 */
export function companyRows(sections: DiagnosticSections): CompanyRow[] {
  if (isUnknown(envelopeOf(sections, "org_structure"))) return [];

  return list(dataOf(sections, "org_structure").company_codes).map((row) => {
    const entry = rec(row);
    return {
      comp_code: text(entry.comp_code),
      comp_name: text(entry.comp_name),
      city: text(entry.city),
      country: text(entry.country),
      currency: text(entry.currency),
    };
  });
}

/**
 * As três unidades que declaram a empresa a que pertencem.
 *
 * ⚠️ **Elas não são igualmente confiáveis, e a diferença é o ponto do card.**
 * `sales_orgs` (TVKO-BUKRS, `rfc.py:329`) e `purchasing_orgs` (T024E-BUKRS,
 * `rfc.py:637`) trazem **BUKRS de verdade**; `plants` traz **BWKEY**, área de
 * avaliação. Achatar as três num vínculo só apagaria justamente a ressalva.
 */
export const ORG_UNIT_KINDS = [
  "plants",
  "sales_orgs",
  "purchasing_orgs",
] as const;
export type OrgUnitKind = (typeof ORG_UNIT_KINDS)[number];

/** Chave de cada entidade dentro da sua linha — o payload não usa um nome comum. */
const ORG_UNIT_CODE_FIELD: Record<OrgUnitKind, string> = {
  plants: "plant",
  sales_orgs: "sales_org",
  purchasing_orgs: "purchasing_org",
};

export interface OrgUnitRow {
  kind: OrgUnitKind;
  code: string | undefined;
  name: string | undefined;
  /** ⚠️ BWKEY em `plants`; BUKRS real em `sales_orgs` e `purchasing_orgs`. */
  company_code: string | undefined;
}

/** As unidades de um balde, separadas por tipo — nunca fundidas. */
export type OrgUnits = Record<OrgUnitKind, OrgUnitRow[]>;

export interface CompanyUnitGroup {
  comp_code: string;
  comp_name: string | undefined;
  units: OrgUnits;
}

export interface OrgHierarchy {
  groups: CompanyUnitGroup[];
  /** Código de empresa preenchido, nenhuma empresa correspondente na lista. */
  unmatched: OrgUnits;
  /** Campo de empresa vazio na origem. */
  noCode: OrgUnits;
  /**
   * A lista de empresas é amostra (`_ORG_SAMPLE` = 100 contra `counts`). Quando é,
   * `unmatched` pode ser artefato da amostragem, e o card tem de dizer isso em vez
   * de acusar a unidade.
   */
  companiesSampled: boolean;
}

function emptyUnits(): OrgUnits {
  return { plants: [], sales_orgs: [], purchasing_orgs: [] };
}

/**
 * A hierarquia empresa → unidades, em **três** buckets.
 *
 * ⚠️ `plants[].company_code` é **BWKEY** (área de avaliação, `T001W-BWKEY`), não
 * BUKRS (`rfc.py:381,393`). Na configuração padrão de avaliação por centro, BWKEY
 * == WERKS — medido no fixture real: as 3 plantas têm `company_code == plant`, e
 * as 2 que casam com empresa casam por **coincidência numérica** (planta `0001`,
 * empresa `0001`). Por isso a ressalva é texto do card, não tooltip: é a única
 * armadilha desta aba capaz de fazer o cliente errar decisão de escopo.
 *
 * As organizações de vendas e de compras entram porque são o **contraponto**: os
 * dois únicos vínculos empresa→unidade que a origem afirma corretamente. Ficam
 * separadas das plantas por tipo justamente para a ressalva não vazar sobre elas.
 *
 * E por isso "órfã" se desdobra em dois: um bucket só juntaria "código que não
 * casa" com "unidade sem código" e afirmaria orfandade onde o que existe é campo
 * errado ou campo em branco.
 */
export function orgUnitsByCompany(sections: DiagnosticSections): OrgHierarchy {
  const closed: OrgHierarchy = {
    groups: [],
    unmatched: emptyUnits(),
    noCode: emptyUnits(),
    companiesSampled: false,
  };
  if (isUnknown(envelopeOf(sections, "org_structure"))) return closed;

  const data = dataOf(sections, "org_structure");
  const companies = companyRows(sections);
  const declared = countMap(data.counts).company_codes;

  const known = new Set(
    companies.flatMap((company) =>
      company.comp_code != null ? [company.comp_code] : [],
    ),
  );
  const grouped = new Map<string, OrgUnits>();
  const unmatched = emptyUnits();
  const noCode = emptyUnits();

  for (const kind of ORG_UNIT_KINDS) {
    for (const row of list(data[kind])) {
      const entry = rec(row);
      const unit: OrgUnitRow = {
        kind,
        code: text(entry[ORG_UNIT_CODE_FIELD[kind]]),
        name: text(entry.name),
        company_code: text(entry.company_code),
      };
      if (unit.company_code == null) {
        noCode[kind].push(unit);
      } else if (known.has(unit.company_code)) {
        let bucket = grouped.get(unit.company_code);
        if (!bucket) {
          bucket = emptyUnits();
          grouped.set(unit.company_code, bucket);
        }
        bucket[kind].push(unit);
      } else {
        unmatched[kind].push(unit);
      }
    }
  }

  return {
    // Ordem de aparição da origem, não alfabética: reordenar seria afirmar um
    // critério que o payload não tem. Empresa sem unidade nenhuma não vira grupo
    // vazio — o card responde "onde estão as unidades", e um grupo sem linha é
    // ruído. Mas empresa **sem planta e com organização de vendas** entra: era
    // invisível quando o card só sabia falar de plantas.
    groups: companies.flatMap((company) => {
      const units =
        company.comp_code != null ? grouped.get(company.comp_code) : undefined;
      return units
        ? [
            {
              comp_code: company.comp_code as string,
              comp_name: company.comp_name,
              units,
            },
          ]
        : [];
    }),
    unmatched,
    noCode,
    companiesSampled: (declared ?? companies.length) > companies.length,
  };
}

// --------------------------------------------------------------------------
// volumetria das tabelas-chave
// --------------------------------------------------------------------------

export interface DataVolumeRow {
  table: string | undefined;
  description: string | undefined;
  /** `undefined` quando não foi contada — nunca `0`, nunca `-1` na tela. */
  rowCount: number | undefined;
  counted: boolean;
  /** A varredura parou no teto: `rowCount` é **piso**, não total. */
  truncated: boolean;
  method: string | undefined;
  cap: number | undefined;
  error: string | undefined;
  /**
   * `false` = contagem de **todos** os mandantes da tabela física (o caso do
   * `method: "fm"`, que não filtra `MANDT`). `undefined` quando o payload não
   * declarou.
   */
  clientScoped: boolean | undefined;
}

export interface DataVolumeSummary {
  rows: DataVolumeRow[];
  client: string | undefined;
  /** `undefined` quando `countingMethod === "mixed"` — ver abaixo. */
  totalRecords: number | undefined;
  countingMethod: string | undefined;
  budgetExhausted: boolean;
  /**
   * `true` quando alguma tabela contada parou no teto de linhas: `totalRecords`
   * é piso, não soma exata. Distinto de `budgetExhausted` (orçamento de
   * *tempo* — uma tabela pode bater o teto de linhas com tempo de sobra).
   */
  totalRecordsIsFloor: boolean;
  totalsByMethod: Record<string, number>;
}

/**
 * A volumetria real das 10 tabelas-chave — a **única** do payload.
 *
 * ⚠️ Não confundir com `master_data.profiles[].total_records`, que satura em
 * **1.000** por construção (`rfc.py:2668` lê com `max_rows=1000` e devolve
 * `total = len(rows)`): no fixture real as 6 tabelas estão todas em `1000`, então
 * o engano não é hipotético. Aquele número não entra neste card.
 *
 * `totalRecords` desaparece em `mixed` porque nesse caso o total soma escopos de
 * mandante diferentes — `fm` conta a tabela física, `scan` filtra `MANDT`, e os
 * dois **não somam**. O próprio modelo do connector avisa que não deve ser
 * apresentado como número único; devolver `undefined` faz a garantia ser do
 * seletor, não da convenção de quem renderiza.
 */
export function dataVolumeRows(
  sections: DiagnosticSections,
): DataVolumeSummary {
  const empty: DataVolumeSummary = {
    rows: [],
    client: undefined,
    totalRecords: undefined,
    countingMethod: undefined,
    budgetExhausted: false,
    totalRecordsIsFloor: false,
    totalsByMethod: {},
  };
  if (isUnknown(envelopeOf(sections, "data_volumes"))) return empty;

  const data = dataOf(sections, "data_volumes");
  const countingMethod = text(data.counting_method);

  return {
    rows: list(data.volumes).map((row) => {
      const entry = rec(row);
      const counted = entry.counted === true;
      const rowCount = num(entry.row_count);
      return {
        table: text(entry.table),
        description: text(entry.description),
        // `-1` é o marcador de "não contada" do connector, e contradição no
        // payload (`counted: true` com `-1`) resolve para o lado do `—`:
        // negativo nunca é contagem.
        rowCount:
          counted && rowCount != null && rowCount >= 0 ? rowCount : undefined,
        counted,
        truncated: entry.truncated === true,
        method: text(entry.method),
        cap: num(entry.cap),
        error: text(entry.error),
        clientScoped:
          typeof entry.client_scoped === "boolean"
            ? entry.client_scoped
            : undefined,
      };
    }),
    client: text(data.client),
    totalRecords:
      countingMethod === "mixed" ? undefined : num(data.total_records),
    countingMethod,
    budgetExhausted: data.budget_exhausted === true,
    totalRecordsIsFloor: data.total_records_is_floor === true,
    totalsByMethod: countMap(data.totals_by_method),
  };
}

// --------------------------------------------------------------------------
// a faixa de seis números
// --------------------------------------------------------------------------

/** Cinco dias úteis por semana. A única constante da aba — declarada, não inline. */
export const WORK_WEEK_DAYS = 5;

/**
 * Semanas de **desenvolvimento**, ou `undefined`.
 *
 * Conversão pura de unidade: `total_effort_days ÷ 5`. Não é duração de projeto, e
 * a célula que a exibe carrega a premissa no subtítulo — sem ele é número sem
 * contexto.
 *
 * ⚠️ **Não lê `by_profile_seniority`.** O agregado é uma segunda partição da
 * *mesma* soma (medido no fixture real: os seis perfis somam `90,75`, o total
 * exato), então `total ÷ nº de perfis` não é duração — é aritmética sem
 * referente. E `total_effort_days` é o somatório WRICEF por construção
 * (`effort_estimator.py:330-337` soma total e partição no mesmo laço): cutover,
 * migração de dados, teste integrado e treinamento não estão aqui. Por isso o
 * rótulo é `DEV EFFORT` e nunca "go-live".
 */
export function devEffortWeeks(
  sections: DiagnosticSections,
): number | undefined {
  if (isUnknown(envelopeOf(sections, "cost_breakdown"))) return undefined;
  const days = num(dataOf(sections, "cost_breakdown").total_effort_days);
  if (days == null) return undefined;
  return Math.round(days / WORK_WEEK_DAYS);
}

/**
 * As seis células da faixa, na ordem do design.
 *
 * Cada uma lê a **sua** seção e passa pelo `sectionCounter` da seção: uma seção
 * fechada rende `—` na sua célula sem contaminar as outras cinco. É o que faz a
 * aba renderizar normalmente num projeto sem Architect.
 *
 * As duas células de organização vêm de `orgEntityCounters`, não de `counts`
 * direto: ler `counts` mostrava `0` empresas quando a extração de empresas falhou
 * — o mesmo zero inventado que o critério 1 do #465 proíbe, uma aba antes.
 */
// --------------------------------------------------------------------------
// visão geral do ambiente (Ato 1, #873)
// --------------------------------------------------------------------------

export interface EnvironmentOverview {
  activeUsers: Metric;
  interfaces: Metric;
  /** Só o IDoc tem `record_count`: este é o denominador honesto de "com tráfego". */
  interfacesMeasured: Metric;
  /** `{rfc, file, idoc}` de objetos que integram, ou `undefined` em run antiga. */
  integrationKinds?: Record<string, number>;
  interfacesWithTraffic: Metric;
}

/**
 * Quantos objetos integram, por tipo de sinal (#939).
 *
 * Sai do catálogo WRICEF, não do inventário de integrações: `integrations` traz
 * o que está **cadastrado** (destino do SM59, tipo de mensagem IDoc), e isto
 * traz o que o código **faz**. No appliance são 22 contra 890.
 *
 * `undefined` distingue "run anterior à migration 0135" de "varreu e não achou",
 * que seria `{rfc: 0, file: 0, idoc: 0}`.
 */
export function integrationKindCounts(
  sections: DiagnosticSections,
): Record<string, number> | undefined {
  if (isUnknown(envelopeOf(sections, "wricef_catalog"))) return undefined;
  const raw = dataOf(sections, "wricef_catalog").by_integration_kind;
  if (raw == null || typeof raw !== "object") return undefined;
  return countMap(raw);
}

/**
 * Os indicadores de abertura do Ato 1 que saem das seções. Empresas e scope
 * items ativos saem do bloco `validation` (`companiesTile`, `scopeItemsTile`).
 *
 * Cada célula lê a **sua** seção, e não um agregado só: fechar uma seção não pode
 * apagar o número de outra, que vem de outra origem e continua conhecido.
 *
 * ⚠️ `interfacesMeasured` existe porque o desenho pede "N ativas de M" e o dado
 * não sustenta esse M. Destination RFC não carrega sinal de tráfego; só o IDoc
 * tem `record_count`. O denominador é a parte medível, e a tela declara isso.
 */
export function environmentOverview(
  sections: DiagnosticSections,
  format: NumberFormat = String,
): EnvironmentOverview {
  const exec = envelopeOf(sections, "executive_summary");
  const execData = dataOf(sections, "executive_summary");
  const integrations = envelopeOf(sections, "integrations");
  const integrationsData = dataOf(sections, "integrations");

  return {
    activeUsers: metric(exec, num(execData.total_active_users), format),
    interfaces: metric(integrations, num(integrationsData.total), format),
    // #939: quantos objetos **chamam** integração no código, por tipo de sinal.
    // `undefined` quando a run é anterior à migration 0135 — e ausência não
    // pode virar "este cliente não integra nada".
    integrationKinds: integrationKindCounts(sections),
    interfacesMeasured: metric(
      integrations,
      num(integrationsData.measured_total),
      format,
    ),
    interfacesWithTraffic: metric(
      integrations,
      num(integrationsData.with_traffic),
      format,
    ),
  };
}

/**
 * O que o tile "Empresas" sabe dizer (EF-DIAG-002, #1342).
 *
 * - `classified`: a regra de empresa-modelo separou as do cliente, e o número
 *   existe.
 * - `unclassified`: as company codes foram lidas, mas sem a separação. Mostrar
 *   o total lido como "do cliente" contaria as empresas-modelo da SAP.
 * - `empty`: a extração existe e não trouxe company code. Todo SAP tem ao menos
 *   uma, então isso é leitura que falhou, e não um zero medido.
 * - `failed`: a leitura do bloco falhou (`error`, `unauthorized`). A extração
 *   pode existir, então mandar esperar outra análise seria afirmar o que não se sabe.
 * - `unknown`: não houve extração, ou o bloco não chegou.
 */
export type CompaniesTile =
  | { kind: "classified"; client: number; inScope: number }
  | { kind: "unclassified"; read: number }
  | { kind: "empty" }
  | { kind: "failed" }
  | { kind: "unknown" };

/**
 * Lê o envelope `org_units` do bloco `validation`, e não uma seção: a decisão do
 * consultor é lida ao vivo pelo backend, também com a Calibragem publicada.
 */
export function companiesTile(
  validation: DiagnosticSnapshot["validation"] | undefined,
): CompaniesTile {
  const envelope = validation?.org_units;
  if (!envelope || envelope.state === "not_collected")
    return { kind: "unknown" };
  if (envelope.state === "empty") return { kind: "empty" };
  if (envelope.state !== "ok") return { kind: "failed" };

  const counts = rec(rec(envelope.data).company_codes);
  const client = num(counts.client);
  const inScope = num(counts.in_scope);
  const read = num(counts.read);
  if (client != null && inScope != null)
    return { kind: "classified", client, inScope };
  if (read != null) return { kind: "unclassified", read };
  return { kind: "unknown" };
}

// --------------------------------------------------------------------------
// três pontos fora do padrão
// --------------------------------------------------------------------------

/** Da cobertura mais grave para a menos grave. */
const GAP_SEVERITY: readonly string[] = [
  "no_coverage",
  "separate_product",
  "compatibility_pack",
  "grow_addon",
];

export interface GapRow {
  scope_item_id: string | undefined;
  scope_item_name: string | undefined;
  lob: string | undefined;
  coverage_type: string | undefined;
  sku_or_product: string | undefined;
  indicative_note: string | undefined;
  /** `["not_in_grow"]` / `["migration_disposition"]` — token, nunca frase. */
  gap_reasons: string[];
  /** Números de Nota SAP. `[]` é o dado: a disposição não citou nota. */
  sap_notes: string[];
  reference: string | undefined;
  out_of_scope: boolean | undefined;
  /** Só `grow_addon` tem preço firme; nos outros é `undefined`, nunca `0`. */
  unit_price_brl: number | undefined;
  /** `grow_sku | indicative | out_of_scope`, ou `undefined` se não casou. */
  pricing_basis: string | undefined;
}

/** Mantido para o consumidor do DIAG.3; a linha é a mesma desde o DIAG.5. */
export type StandoutGap = GapRow;

/**
 * **Todos** os gaps da amostra, do mais grave para o menos.
 *
 * ⚠️ Sai de `coverage_gaps[]`, que é **amostra** capada em 100 contra o total de
 * `counts.coverage_gaps`. O card tem de rotular isso — o `DiagnosticSection` já
 * mostra a linha de amostragem a partir do `truncated` do envelope.
 *
 * A ordem é por `coverage_type`, o token real. Não existe coluna de severidade no
 * payload; inventar uma seria o mesmo erro que inventar zero.
 *
 * ⚠️ `unit_price_brl` e `pricing_basis` chegam por **join** do backend
 * (`_gap_prices`), não pela lista de origem: `scope_activation.gaps` não é
 * precificada. Ausentes os dois quando o gap não casa com
 * `bom_summary.gaps_coverage` — e aí a aba diz "a cotar", não "R$ 0".
 */
export function gapRows(sections: DiagnosticSections): GapRow[] {
  if (isUnknown(envelopeOf(sections, "scope_gaps"))) return [];

  const rank = (row: GapRow) => {
    const index = GAP_SEVERITY.indexOf(row.coverage_type ?? "");
    // Token desconhecido vai para o fim, não para o topo: o card não pode
    // promover a destaque algo que não sabe ler.
    return index === -1 ? GAP_SEVERITY.length : index;
  };

  return list(dataOf(sections, "scope_gaps").coverage_gaps)
    .map((row) => {
      const entry = rec(row);
      return {
        scope_item_id: str(entry.scope_item_id),
        scope_item_name: str(entry.scope_item_name),
        lob: str(entry.lob),
        coverage_type: str(entry.coverage_type),
        sku_or_product: str(entry.sku_or_product),
        indicative_note: str(entry.indicative_note),
        gap_reasons: strings(entry.gap_reasons),
        sap_notes: strings(entry.sap_notes),
        reference: text(entry.reference),
        out_of_scope: bool(entry.out_of_scope),
        unit_price_brl: num(entry.unit_price_brl),
        pricing_basis: text(entry.pricing_basis),
      };
    })
    .sort((a, b) => rank(a) - rank(b));
}

export interface SimplificationRow {
  /** O código do Simplification Item, sem o componente. */
  id?: string;
  /** A área SAP que dispara o item, quando o payload a declara. */
  component?: string;
  title?: string;
  /** Número da nota SAP, sem os zeros à esquerda do formato interno. */
  note?: string;
}

/**
 * Os Simplification Items que o uso do cliente dispara.
 *
 * Chegam em `scope_gaps.migration_gaps` desde a FUN.1 e nenhum card os lia.
 * Medido em 24/09/2026 nos dois projetos da demonstração: `coverage_gaps` é
 * zero e estes são três, então eles são o que o Ato 3 tem para mostrar.
 *
 * `sitem_id` carrega duas informações num campo só (`SI20: SD_SLS`): o item e o
 * componente. A tela mostra os dois separados, porque concatenados a área
 * desaparece no meio do código.
 */
export function migrationSimplifications(
  sections: DiagnosticSections,
): SimplificationRow[] {
  if (isUnknown(envelopeOf(sections, "scope_gaps"))) return [];

  return list(dataOf(sections, "scope_gaps").migration_gaps)
    .map((entry) => {
      const row = rec(entry);
      const [id, component] = (str(row.sitem_id) ?? "").split(":", 2);
      return {
        id: id.trim() || undefined,
        component: component?.trim() || undefined,
        title: str(row.title),
        // O payload traz `0002355169`; o que se digita na busca da SAP é
        // `2355169`. Mostrar o formato interno faria o número parecer outro.
        note: (str(row.note) ?? "").replace(/^0+/, "") || undefined,
      };
    })
    .filter((row) => row.id !== undefined || row.title !== undefined);
}

/**
 * Quantos Simplification Items a execução achou, contra os que a tela mostra.
 *
 * `migration_gaps` é amostrado em 100 no builder e `counts.migration_gaps`
 * carrega o total. Sem o par, cem linhas seriam apresentadas como a lista
 * inteira.
 */
export function simplificationTotal(
  sections: DiagnosticSections,
): number | undefined {
  if (isUnknown(envelopeOf(sections, "scope_gaps"))) return undefined;
  return num(rec(dataOf(sections, "scope_gaps").counts).migration_gaps);
}

/**
 * Os três do card de destaque — o mesmo corte de {@link gapRows}, menos o item
 * cujo único motivo de gap é ter Simplification Item na migração e que não casou
 * com cobertura nenhuma.
 *
 * Esse item não é lacuna do padrão: é nota de migração, que tem card próprio no
 * mesmo ato. Destacado, punha Contas a Receber e Contas a Pagar da AL5 como "Sem
 * cobertura". O item de simplificação que casou com cobertura (um pacote de
 * compatibilidade, por exemplo) continua: aí há o que tratar.
 */
export function standoutGaps(
  sections: DiagnosticSections,
  limit = 3,
): GapRow[] {
  const onlySimplification = (row: GapRow) =>
    row.coverage_type === "no_coverage" &&
    row.gap_reasons.length > 0 &&
    row.gap_reasons.every((reason) => reason === "migration_disposition");
  return gapRows(sections)
    .filter((row) => !onlySimplification(row))
    .slice(0, limit);
}

// --------------------------------------------------------------------------
// itens no padrão e a sua evidência
// --------------------------------------------------------------------------

export interface CapabilityEvidence {
  capability: string | undefined;
  s4Target: string | undefined;
  /** As tabelas que dispararam **esta** capacidade, não as do item inteiro. */
  tables: string[];
}

export interface ScopeEvidence {
  /**
   * `triggered_by`: nomes de tabela (`BSEG`), predicados de campo
   * (`SKB1.XSPEB = X`) e gates de país (`country=BR`). **Nunca transações** — o
   * motor não as produz, e rotular presença de tabela como uso medido é a mentira
   * que o critério 5 da task proíbe.
   */
  tables: string[];
  capabilities: CapabilityEvidence[];
  /**
   * `undefined` quando a run é anterior ao DIAG.8 — "não dá para saber".
   * `[]` **com** `observedSource` é "cruzou o catálogo com o uso e não achou".
   * Os dois casos rendem copy diferente, e é por isso que não colapsam.
   */
  observedTcodes: string[] | undefined;
  observedSource: string | undefined;
  quality: string | undefined;
  /** O cap **por item** do backend. Distinto do `truncated` da seção. */
  truncated: boolean;
}

export interface ActivatedItem {
  scope_item_id: string | undefined;
  scope_item_name: string | undefined;
  lob: string | undefined;
  tier: string | undefined;
  confidence: string | undefined;
  clean_core: boolean | undefined;
  is_in_grow: boolean | undefined;
  evidence: ScopeEvidence;
}

export interface ActivatedItems {
  rows: ActivatedItem[];
  /** Quantos a grid mostra. */
  shown: number;
  /**
   * Quantos existem — de `bom_totals.requirements_total`, o mesmo denominador
   * do donut e do KPI de Standard Fit (o recorte aprovado). Nunca `rows.length`:
   * a amostra é capada em 200, e igualar os dois faria a grid afirmar que o
   * cliente aprovou só o que coube na amostra.
   */
  total: number | undefined;
}

function itemEvidence(entry: Record<string, unknown>): ScopeEvidence {
  return {
    tables: strings(entry.triggered_by),
    capabilities: list(entry.capability_details).map((raw) => {
      const detail = rec(raw);
      return {
        capability: text(detail.capability),
        s4Target: text(detail.s4_target),
        tables: strings(detail.triggered_by),
      };
    }),
    // `in` e não `?? undefined`: a distinção inteira mora na presença da chave.
    observedTcodes:
      "observed_tcodes" in entry ? strings(entry.observed_tcodes) : undefined,
    observedSource: text(entry.observed_tcodes_source),
    quality: text(entry.evidence_quality),
    truncated: entry.evidence_truncated === true,
  };
}

/**
 * Os scope items aprovados como `activate`, com a evidência que os justifica.
 *
 * A allowlist já chega filtrada do backend contra
 * `mygrow.project_scope_approval_item` (`_active_scope_items`,
 * `diagnostic_builder.py`) — não há filtro nenhum aqui por cima. Antes o
 * corte era `verdict === "activate"`, o vocabulário do motor ECC; isso é outro
 * eixo do `EligibilityVerdict` da aprovação (`activate | decision |
 * out_of_scope`), e um item pode ativar num sem ter sido aprovado no outro.
 */
export function activatedItems(sections: DiagnosticSections): ActivatedItems {
  if (isUnknown(envelopeOf(sections, "scope_activation"))) {
    return { rows: [], shown: 0, total: undefined };
  }

  const data = dataOf(sections, "scope_activation");
  const rows = list(data.items)
    .map(rec)
    .map((entry) => ({
      scope_item_id: str(entry.scope_item_id),
      scope_item_name: text(entry.scope_item_name),
      lob: text(entry.lob),
      tier: text(entry.tier),
      confidence: text(entry.confidence),
      clean_core: bool(entry.clean_core),
      is_in_grow: bool(entry.is_in_grow),
      evidence: itemEvidence(entry),
    }));

  const total = isUnknown(envelopeOf(sections, "bom_totals"))
    ? undefined
    : num(dataOf(sections, "bom_totals").requirements_total);

  return { rows, shown: rows.length, total };
}

/** `activation_map_version` — trafega como `version` no payload. */
export function activationVersion(
  sections: DiagnosticSections,
): string | undefined {
  if (isUnknown(envelopeOf(sections, "scope_activation"))) return undefined;
  return text(dataOf(sections, "scope_activation").version);
}

export interface EvidenceCoverage {
  itemsWithKeyTcodes: number;
  total: number;
}

/**
 * Sobre quantos itens dá para afirmar algo a partir de **uso real**.
 *
 * Não é "quantos o cliente usa": item do tier RAG nasce sem `key_tcodes` e nunca
 * teria o que cruzar. O backend emite os dois como `null` quando a run não
 * produziu o bloco, e aí a resposta é `undefined` — `0 de 0` leria como "nada
 * evidenciado", que é uma afirmação, não uma ausência.
 */
export function evidenceCoverage(
  sections: DiagnosticSections,
): EvidenceCoverage | undefined {
  if (isUnknown(envelopeOf(sections, "scope_activation"))) return undefined;

  const block = rec(dataOf(sections, "scope_activation").evidence_coverage);
  const itemsWithKeyTcodes = num(block.items_with_key_tcodes);
  const total = num(block.total);
  if (itemsWithKeyTcodes == null || total == null) return undefined;
  return { itemsWithKeyTcodes, total };
}

/**
 * Preço em reais, no idioma do usuário.
 *
 * Era `en-US` fixo, e a razão registrada era boa: o resto do módulo formatava
 * assim, e misturar locales deixaria `1.234,56` numa célula e `663,816` na
 * vizinha. O que mudou foi o leitor. Numa devolutiva em português,
 * `R$ 91,177,776.45` não se lê na primeira passada.
 *
 * A consistência do módulo continua garantida, só que pelo outro lado: todo
 * número desta tela passa a seguir o mesmo idioma, via {@link formatCount} e
 * daqui. Duas casas porque a coluna é preço unitário, e centavo de SKU muda o
 * total.
 */
export function formatBrl(value: number): string {
  return `R$ ${formatCount(value, { decimals: 2 })}`;
}

export interface LicenseSegment {
  code: string;
  /** `license_type_name` de algum usuário da amostra com esse código — melhor
   * esforço, decoração. Se nenhum trouxer o nome, cai para o próprio código. */
  label: string;
  value: number;
}

export interface ModuleLicenseBreakdown {
  module: string;
  total: number;
  /** Mesmos códigos/ordem de {@link LicenseAssignmentOverview.segments}, para a
   * cor do segmento casar entre a barra do todo e a barra do módulo. */
  segments: LicenseSegment[];
}

export type UsageProfileBucket =
  "operational" | "managerial" | "self_service" | "technical";

/** A ordem fixa dos quatro perfis, para filtro e legenda fora do card. */
export const USAGE_PROFILE_ORDER: UsageProfileBucket[] = [
  "operational",
  "self_service",
  "technical",
  "managerial",
];

export interface UsageProfileRow {
  bucket: UsageProfileBucket;
  count: number;
  /** Fração da população mapeada. `undefined` quando o total é zero. */
  share: number | undefined;
}

export interface UsageProfile {
  rows: UsageProfileRow[];
  total: number;
  /** Quantos usuários o waterfall classificou num tipo que não cai em balde
   * nenhum. Declarado em vez de somido: tipo novo no seed entraria em silêncio
   * e o card deixaria de fechar com a população. */
  unmapped: number;
}

/**
 * Perfil de uso dos usuários ativos (#682), nos quatro baldes do desenho.
 *
 * A dobra dos códigos de licença nos quatro perfis é do servidor
 * (`services/api/app/services/usage_profile.py`, #1770), que a anexa em
 * `user_sku_sample.data.usage_profile` nos dois caminhos da busca, calibrado e
 * corrente. O PDF do cliente usa a mesma conta: uma regra só, o mesmo número.
 * Sem o perfil pronto, o card se fecha em vez de refazer a conta aqui.
 */
export function usageProfile(
  sections: DiagnosticSections,
): UsageProfile | undefined {
  if (isUnknown(envelopeOf(sections, "user_sku_sample"))) return undefined;

  const raw = dataOf(sections, "user_sku_sample").usage_profile as
    | { rows?: unknown; total?: unknown; unmapped?: unknown }
    | undefined;
  if (!raw || !Array.isArray(raw.rows) || typeof raw.total !== "number") return undefined;

  const rows: UsageProfileRow[] = [];
  for (const row of raw.rows as { bucket?: unknown; count?: unknown; share?: unknown }[]) {
    if (!USAGE_PROFILE_ORDER.includes(row.bucket as UsageProfileBucket)) continue;
    if (typeof row.count !== "number") continue;
    rows.push({
      bucket: row.bucket as UsageProfileBucket,
      count: row.count,
      share: typeof row.share === "number" ? row.share : undefined,
    });
  }
  return {
    rows,
    total: raw.total,
    unmapped: typeof raw.unmapped === "number" ? raw.unmapped : 0,
  };
}

export interface LicenseAssignmentOverview {
  /** População inteira já mapeada — `user_sku_sample.total_mapped`. */
  totalMapped: number | undefined;
  /** Quantos, da população, seguem ambíguos — pedem revisão manual. */
  ambiguousCount: number | undefined;
  /** Da seção **irmã** `pending_review`: população que não resolveu licença
   * nenhuma, não os ambíguos que já resolveram (tentativamente). */
  pendingReviewCount: number | undefined;
  /** Barra do todo — de `license_type_distribution`, agregado sobre a
   * **população**, não a amostra. */
  segments: LicenseSegment[];
  /** Quebra por módulo primário (o primeiro em ordem alfabética dos módulos do
   * usuário) — sai só da amostra de 50, e é por isso que o card precisa dizer
   * isso: um módulo raro na amostra pode estar super ou sub-representado. */
  byModule: ModuleLicenseBreakdown[];
  sampleShown: number;
  sampleTotal: number | undefined;
}

/** Módulo usado para a quebra secundária — simplificação declarada: usuário
 * com mais de um módulo entra só pelo primeiro (ordem alfabética). */
function primaryModule(modules: string[]): string {
  return modules.length > 0 ? [...modules].sort()[0] : "_UNASSIGNED";
}

/**
 * O panorama de atribuição de licença: barra do todo (população) + quebra por
 * módulo primário (amostra) + os dois totais de "resolvido"/"pendente".
 *
 * Duas fontes bem distintas viajam juntas de propósito, e o card precisa dizer
 * qual é qual: `segments` fecha contra a população (`license_type_distribution`
 * é agregado sobre todos os usuários mapeados); `byModule` só existe sobre os 50
 * da amostra, porque `modules[]` por usuário não é allowlistado fora dela.
 */
export function licenseAssignmentOverview(
  sections: DiagnosticSections,
): LicenseAssignmentOverview {
  const empty: LicenseAssignmentOverview = {
    totalMapped: undefined,
    ambiguousCount: undefined,
    pendingReviewCount: undefined,
    segments: [],
    byModule: [],
    sampleShown: 0,
    sampleTotal: undefined,
  };
  const envelope = envelopeOf(sections, "user_sku_sample");
  if (isUnknown(envelope)) return empty;

  const data = dataOf(sections, "user_sku_sample");
  const distribution = countMap(data.license_type_distribution);
  const sampleRows = list(data.sample).map(rec);

  const nameByCode = new Map<string, string>();
  for (const row of sampleRows) {
    const rowCode = code(row.license_type_code);
    const name = str(row.license_type_name);
    if (rowCode != null && name != null && !nameByCode.has(rowCode))
      nameByCode.set(rowCode, name);
  }

  const codes = Object.keys(distribution).sort(
    (a, b) => distribution[b] - distribution[a],
  );
  const segments: LicenseSegment[] = codes.map((licenseCode) => ({
    code: licenseCode,
    label: nameByCode.get(licenseCode) ?? licenseCode,
    value: distribution[licenseCode],
  }));

  const byModuleCounts = new Map<string, Map<string, number>>();
  for (const row of sampleRows) {
    const rowCode = code(row.license_type_code);
    if (rowCode == null) continue;
    const module = primaryModule(strings(row.modules));
    const bucket = byModuleCounts.get(module) ?? new Map<string, number>();
    bucket.set(rowCode, (bucket.get(rowCode) ?? 0) + 1);
    byModuleCounts.set(module, bucket);
  }
  const byModule: ModuleLicenseBreakdown[] = Array.from(
    byModuleCounts.entries(),
  )
    .map(([module, bucket]) => ({
      module,
      total: Array.from(bucket.values()).reduce((acc, n) => acc + n, 0),
      segments: codes
        .filter((licenseCode) => bucket.has(licenseCode))
        .map((licenseCode) => ({
          code: licenseCode,
          label: nameByCode.get(licenseCode) ?? licenseCode,
          value: bucket.get(licenseCode) ?? 0,
        })),
    }))
    .sort((a, b) => b.total - a.total);

  const pendingEnvelope = envelopeOf(sections, "pending_review");
  const truncation = envelope?.truncated;

  return {
    totalMapped: num(data.total_mapped),
    ambiguousCount: num(data.ambiguous_count),
    pendingReviewCount: isUnknown(pendingEnvelope)
      ? undefined
      : num(dataOf(sections, "pending_review").count),
    segments,
    byModule,
    sampleShown: truncation?.shown ?? sampleRows.length,
    sampleTotal: truncation?.total ?? num(data.total_mapped),
  };
}

// --------------------------------------------------------------------------
// investimento (DIAG.7) — composição, esforço e licenças
// --------------------------------------------------------------------------

export interface InvestmentComposition {
  monthly: Metric;
  annual: Metric;
  skuLines: Metric;
  /** `Σ effective_count` — contagem **faturável**, não gente (Correção 6). */
  billableCount: Metric;
}

/**
 * As quatro células de software do card de Composição, de `bom_totals`.
 *
 * Reusa `formatBrl` para as duas células monetárias; `skuLines`/`billableCount`
 * são contagem simples, formato distinto por célula. O valor do projeto saiu
 * daqui com a EF-FUNC-012: vem da Calibragem comercial (`commercialOffer`).
 */
export function investmentComposition(
  sections: DiagnosticSections,
): InvestmentComposition {
  const bom = envelopeOf(sections, "bom_totals");
  const bomData = dataOf(sections, "bom_totals");
  const count: NumberFormat = (n) => formatCount(n);

  return {
    monthly: metric(bom, num(bomData.monthly_grand_total), formatBrl),
    annual: metric(bom, num(bomData.annual_grand_total), formatBrl),
    skuLines: metric(bom, num(bomData.total_sku_lines), count),
    billableCount: metric(bom, num(bomData.total_users_in_bom), count),
  };
}

export interface LicenseScenarioLine {
  sku: string;
  listQuantity: number;
  optimizedQuantity: number;
  listCost: number | undefined;
  optimizedCost: number | undefined;
  savings: number | undefined;
  /** Vocabulário fechado da S-080: `tier_downgrade`, `combo_consolidation`,
   * `block_fill`, `self_service`, `none`. */
  lever: string;
  /** Quantas pessoas distintas a linha move. `affected_movements` conta
   * assento, e os dois divergem quando o payload repete um username. */
  affectedCount: number;
}

export interface LicenseScenario {
  /** `false` quando o catálogo de preço não foi carregado. **Não é economia
   * zero**: a tela precisa dizer "não calculado", e o backend distingue os dois
   * de propósito (#682). */
  computed: boolean;
  listTotal: Metric;
  optimizedTotal: Metric;
  /** Economia das alavancas, medida na capacidade real. */
  savings: Metric;
  /** A parcela da diferença que vem do mínimo contratual, e **não** é desconto.
   * `lista − otimizado === economia + bloco`. */
  blockEffect: Metric;
  /** Fração do arranjo com preço conhecido, pesada por assento. A tela imprime
   * a ressalva junto do dinheiro quando ela não é 1. */
  pricedCoverage: number | undefined;
  /** Só as linhas que mudaram entre os dois cenários — as iguais não explicam
   * economia nenhuma e só ocupam a tela. */
  changed: LicenseScenarioLine[];
  /** Usuários fora da conta, e alavancas que não deu para precificar. Somar
   * menos gente que a população sem dizer quanto é silêncio (#753). */
  usersDropped: number | undefined;
  leversDropped: number | undefined;
  /** Pessoas **distintas** que cada alavanca move, direto dos movimentos do
   * motor (#881).
   *
   * Não dá para derivar isto das linhas, e a revisão da #963 provou por quê:
   * `affected_count` da linha conta todos os movimentos que tocam o SKU,
   * qualquer que seja a alavanca deles, e o `lever` da linha é só a mais
   * frequente. Somar duplica (um movimento toca origem e destino) e pegar o
   * máximo subnotifica (duas famílias de rebaixamento rendem quatro linhas):
   * medido, 50 pessoas viravam 30 e 13 viravam 16. */
  usersByLever: { lever: string; users: number }[];
  /** Pessoas distintas movidas por qualquer alavanca. **Não** é a soma do campo
   * acima: a mesma pessoa não aparece em duas, mas somar pressuporia isso. */
  usersMoved: number | undefined;
}

/**
 * Os dois cenários de licenciamento lado a lado (#682, motor da #681).
 *
 * **"Não calculado" e "economia zero" são estados diferentes**, e a issue pede
 * os dois separados: sem catálogo de preço o cenário não existe e a tela não
 * pode mostrar comparação nenhuma; com preço e sem diferença, ele existe e vale
 * zero. A primeira é ausência, a segunda é resposta.
 *
 * `changed` traz só as linhas em que os dois cenários divergem. Linha igual dos
 * dois lados não explica economia e rouba a atenção de quem apresenta.
 */
export function licenseScenario(sections: DiagnosticSections): LicenseScenario {
  const envelope = envelopeOf(sections, "license_scenario");
  const data = dataOf(sections, "license_scenario");
  const computed = data.computed === true && !isUnknown(envelope);

  const lines = Array.isArray(data.scenario_lines) ? data.scenario_lines : [];
  const changed: LicenseScenarioLine[] = [];
  for (const raw of lines) {
    if (raw == null || typeof raw !== "object") continue;
    const line = raw as Record<string, unknown>;
    const listQuantity = num(line.list_quantity) ?? 0;
    const optimizedQuantity = num(line.optimized_quantity) ?? 0;
    const savings = num(line.savings_brl);
    // O corte é pelo **dinheiro**, não pela quantidade cobrada. O motor mede a
    // economia na capacidade resolvida e as quantidades saem depois do mínimo
    // contratual: uma linha cujo piso domina os dois lados tem `list_quantity
    // === optimized_quantity` e economia diferente de zero. Cortando pela
    // quantidade, ela sumia — e a alavanca `block_fill`, cuja definição é
    // justamente folga com quantidade igual, ficava invisível sempre.
    if (
      listQuantity === optimizedQuantity &&
      (savings == null || savings === 0)
    )
      continue;
    const sku = text(line.list_sku);
    if (sku == null) continue;
    changed.push({
      sku,
      listQuantity,
      optimizedQuantity,
      listCost: num(line.list_cost_brl),
      optimizedCost: num(line.optimized_cost_brl),
      savings,
      lever: text(line.lever) ?? "none",
      affectedCount: num(line.affected_count) ?? 0,
    });
  }

  return {
    computed,
    listTotal: metric(envelope, num(data.list_total_brl), formatBrl),
    optimizedTotal: metric(envelope, num(data.optimized_total_brl), formatBrl),
    savings: metric(envelope, num(data.savings_brl), formatBrl),
    blockEffect: metric(envelope, num(data.block_effect_brl), formatBrl),
    pricedCoverage: num(data.priced_coverage),
    changed,
    usersDropped: num(data.users_dropped),
    leversDropped: num(data.levers_dropped),
    usersByLever: Object.entries(countMap(data.users_by_lever))
      .map(([lever, users]) => ({ lever, users }))
      .sort((a, b) => b.users - a.users || a.lever.localeCompare(b.lever)),
    usersMoved: num(data.users_moved),
  };
}

export interface LicensingCompositionRow {
  sku: string | undefined;
  description: string | undefined;
  unitPriceBrl: number | undefined;
  minBlocks: number | undefined;
  isDigitalAccess: boolean;
  basis: string | undefined;
  /** Usuários e faturável. O **faturável** tem os dois lados, e é nele que a
   * tabela desenha a seta do desenho (`46 → 45`).
   *
   * Usuários tem um lado só de propósito: o cenário publica `list_quantity`,
   * que é quantidade **cobrada** depois do mínimo contratual
   * (`license_scenario.py:746`), e não contagem de gente. Não existe contagem
   * de gente do lado da lista, e inventá-la fazia a coluna ler "15 → 8" onde a
   * população subiu de 3 para 8. `undefined` na linha que só existe no cenário,
   * que não tem contagem de lado nenhum.
   *
   * Sem cenário calculado, `listBillable` cai em `effectiveCount` e **não** é
   * `undefined`: quem faz a tabela mostrar uma coluna só é o `show` da
   * `Transition`, ligado a `hasScenario`. O comentário anterior dizia o
   * contrário, e dizia isso de um campo que nem existe mais. */
  users: number | undefined;
  listBillable: number | undefined;
  optimizedBillable: number | undefined;
  listMonthly: number | undefined;
  optimizedMonthly: number | undefined;
  /** `false` na linha que **só** existe do lado da lista: a otimização zerou o
   * SKU, então ele não tem linha de BOM. Ela entra nos totais como as outras e
   * a tabela a desenha à parte, porque não tem preço unitário, mínimo nem as
   * pílulas de Digital Access — nada disso existe fora do BOM. */
  fromBom: boolean;
  /** Os Developer que a franquia do SAP Finance Base cobre. Eles não têm linha
   * de BOM e seguem ativos, então a linha existe para o Total de usuários
   * fechar com o Ato 1 (AL5, 19/10/2026: 57 contra 58). Custo zero dos dois
   * lados, e por isso não mexe nos totais em dinheiro. */
  coveredByAllowance: boolean;
  /** `optimizado − lista`. Negativo economiza, positivo custa, `undefined`
   * quando algum dos dois lados não tem preço. */
  deltaMonthly: number | undefined;
}

export interface LicensingComposition {
  rows: LicensingCompositionRow[];
  /** `true` quando há cenário: a tabela ganha as colunas de comparação. */
  hasScenario: boolean;
  totalUsers: number | undefined;
  /** O faturável somado dos dois lados. É este par que o desenho escreve como
   * `46 → 45` na linha de Total, e é dele que a ressalva de "não é contagem de
   * pessoas" fala: é o número que explica o dinheiro da linha ao lado. */
  totalListBillable: number | undefined;
  totalOptimizedBillable: number | undefined;
  totalListMonthly: number | undefined;
  totalOptimizedMonthly: number | undefined;
  totalDeltaMonthly: number | undefined;
  /** Economia anual, para a faixa de destaque. `undefined` quando parte do
   * arranjo não tem preço: aí o motor anula os totais inteiros. */
  annualSavings: number | undefined;
  /** Usuários que mantiveram a licença mas ficaram sem alavanca explicada. */
  leversDropped: number | undefined;
  /** As alavancas que **apareceram nesta execução**, com quanta gente cada uma
   * moveu. O desenho as escreve em prosa, e nomear alavanca que não ocorreu
   * seria inventar método. */
  levers: { lever: string; affectedCount: number }[];
}

/**
 * A tabela do Ato 5 como o desenho aprovado a define (#682).
 *
 * O protótipo (`Docs/myGrow_redesign`, e a imagem em
 * `Docs/Calibragem_Diagnostico/diag-5.png`) **não** tem um card separado de
 * otimização: ele põe os dois cenários como colunas da mesma tabela de
 * Composição, com `46 → 45` por linha e um Delta assinado. Quem apresenta lê a
 * mudança na própria linha, sem correlacionar dois lugares.
 *
 * A junção é por SKU. O `bom_totals` traz o lado **otimizado**, porque o BOM é
 * o que o nó 05 resolve; o `license_scenario` traz o contrafactual.
 */
export function licensingComposition(
  sections: DiagnosticSections,
): LicensingComposition {
  const lines = licenseLines(sections);
  const scenario = licenseScenario(sections);
  const bySku = new Map(scenario.changed.map((line) => [line.sku, line]));

  const rows: LicensingCompositionRow[] = lines.map((line) => {
    const changed = line.skuNumber ? bySku.get(line.skuNumber) : undefined;
    // Achado da revisão (#682): linha que **mudou** e não tem preço de lista é
    // desconhecida, e não "igual ao otimizado". Caindo em `line.monthlyTotal` a
    // coluna de lista imprimia o valor otimizado como se fosse o da lista e o
    // Delta dava exatamente zero. O BOM tem preço de retaguarda
    // (`resolve_skus.py:1337`) que o cenário não tem, então o caso é real.
    const listMonthly = changed
      ? changed.listCost != null
        ? changed.listCost / 12
        : undefined
      : scenario.computed
        ? line.monthlyTotal
        : undefined;
    return {
      sku: line.skuNumber,
      description: line.description,
      unitPriceBrl: line.unitPriceBrl,
      minBlocks: line.minBlocks,
      isDigitalAccess: line.isDigitalAccess,
      basis: line.basis,
      // `list_quantity` é a quantidade **cobrada**, depois do mínimo contratual
      // (`license_scenario.py:746`), e `user_count` é a contagem resolvida.
      // Uma de cada lado fazia a coluna ler "15 → 8" onde a gente subiu de 3
      // para 8. O cenário não publica a contagem do lado da lista, então a
      // coluna mostra só o que se sabe.
      users: countsPeople(line) ? line.userCount : undefined,
      listBillable: changed ? changed.listQuantity : line.effectiveCount,
      optimizedBillable: line.effectiveCount,
      listMonthly,
      optimizedMonthly: line.monthlyTotal,
      deltaMonthly:
        listMonthly != null && line.monthlyTotal != null
          ? line.monthlyTotal - listMonthly
          : undefined,
      fromBom: true,
      coveredByAllowance: false,
    };
  });

  /*
    O SKU que só existe do lado da lista (#682, achado da revisão).

    `bom_totals.lines` é o lado **otimizado**: o nó percorre os SKUs *cobrados*
    (`resolve_skus.py:1322`), então SKU que a otimização zera não tem linha de
    BOM. Partindo só das linhas de BOM, o caso central do card — todos saem de
    FIN Premium para FIN Base — perdia a linha do Premium, o custo de lista dele
    sumia do Total, e o Delta virava "+R$ 30.525" enquanto a faixa logo abaixo
    anunciava R$ 253.200 de economia. Dois números que se contradizem no mesmo
    card, medido em 27/09/2026.

    A linha órfã sai sem descrição porque o cenário não a carrega
    (`license_scenario.py:177`): mostra o número do SKU, que é o que existe.
  */
  const inBom = new Set(
    rows.map((row) => row.sku).filter((sku): sku is string => sku != null),
  );
  for (const line of scenario.changed) {
    if (inBom.has(line.sku)) continue;
    // Ausência tem duas causas, e só uma é "a otimização zerou o SKU".
    // `bom_totals.lines` é amostra e degrada em (25, 5, 0), enquanto
    // `scenario_lines` está em `_NON_SAMPLE_LISTS` e nunca encolhe — e o
    // `bom_totals` cede primeiro. No degrau intermediário, SKU cortado da
    // amostra voltava como linha afirmando "não cobrado" com quantidade
    // otimizada maior que zero na mesma linha. O discriminador é a quantidade.
    if (line.optimizedQuantity !== 0) continue;
    const listMonthly = line.listCost != null ? line.listCost / 12 : undefined;
    const optimizedMonthly =
      line.optimizedCost != null ? line.optimizedCost / 12 : undefined;
    rows.push({
      sku: line.sku,
      description: undefined,
      unitPriceBrl: undefined,
      minBlocks: undefined,
      isDigitalAccess: false,
      basis: undefined,
      // Nem uma contagem de gente: o cenário só tem quantidade cobrada.
      users: undefined,
      listBillable: line.listQuantity,
      optimizedBillable: line.optimizedQuantity,
      listMonthly,
      optimizedMonthly,
      deltaMonthly:
        listMonthly != null && optimizedMonthly != null
          ? optimizedMonthly - listMonthly
          : undefined,
      fromBom: false,
      coveredByAllowance: false,
    });
  }

  const allowance = developerAllowance(sections);
  if (allowance && !rows.some((row) => row.sku === allowance.sku)) {
    rows.push({
      sku: allowance.sku,
      description: undefined,
      unitPriceBrl: undefined,
      minBlocks: undefined,
      isDigitalAccess: false,
      basis: undefined,
      users: allowance.users,
      listBillable: 0,
      optimizedBillable: 0,
      listMonthly: 0,
      optimizedMonthly: 0,
      deltaMonthly: undefined,
      fromBom: false,
      coveredByAllowance: true,
    });
  }

  const sum =(pick: (r: LicensingCompositionRow) => number | undefined) => {
    const values = rows.map(pick).filter((v): v is number => v != null);
    return values.length ? values.reduce((a, b) => a + b, 0) : undefined;
  };

  /*
    Linha com **um** lado só com preço não totaliza nada (#682, 2ª passada).

    `_cost` devolve `None` sem tabela de faixa (`license_scenario.py:317`) e o
    BOM tem preço de retaguarda (`resolve_skus.py:1337`), então a linha sai com
    `listMonthly` ausente e `optimizedMonthly` cheio. As duas colunas passam a
    somar populações diferentes, e subtrair uma da outra fabricava um Delta
    assinado e colorido — "+R$ 30.525 mais caro" sem nada ter encarecido.

    A regra é a que o motor já usa, e é "faltou **qualquer** lado", não "faltou
    exatamente um": `fully_priced` exige os dois lados em toda linha
    (`license_scenario.py:808`), e sem isso ele anula `list_total` e
    `optimized_total`. A primeira versão desta guarda era XOR e deixava passar
    a linha com os dois lados vazios — SKU `price_status: on_request` não tem
    faixa nem preço de retaguarda, então o BOM sai com `monthly_total` nulo
    (#686). Ela era descartada das duas somas e o Total voltava a imprimir dois
    valores e um Delta assinado, com a faixa logo abaixo dizendo que a
    comparação não foi totalizada. A mesma contradição, por outra porta.
  */
  const anyUnpriced = rows.some(
    (row) => row.listMonthly == null || row.optimizedMonthly == null,
  );
  const totalListMonthly = anyUnpriced ? undefined : sum((r) => r.listMonthly);
  const totalOptimizedMonthly = anyUnpriced
    ? undefined
    : sum((r) => r.optimizedMonthly);

  return {
    rows,
    hasScenario: scenario.computed,
    totalUsers: sum((r) => r.users),
    totalListBillable: sum((r) => r.listBillable),
    totalOptimizedBillable: sum((r) => r.optimizedBillable),
    totalListMonthly,
    totalOptimizedMonthly,
    // A diferença das duas colunas ao lado, e não a soma dos deltas das linhas:
    // somar descarta as linhas sem delta, enquanto cada coluna de dinheiro
    // descarta um subconjunto diferente, e a linha de Total é justamente a que
    // o leitor confere subtraindo o que vê.
    totalDeltaMonthly:
      totalListMonthly != null && totalOptimizedMonthly != null
        ? totalOptimizedMonthly - totalListMonthly
        : undefined,
    annualSavings: scenario.savings.value,
    leversDropped: scenario.leversDropped,
    levers: Object.entries(
      scenario.changed.reduce<Record<string, number>>((acc, line) => {
        if (line.lever === "none") return acc;
        acc[line.lever] = Math.max(acc[line.lever] ?? 0, line.affectedCount);
        return acc;
      }, {}),
    )
      .sort((a, b) => b[1] - a[1])
      .map(([lever, affectedCount]) => ({ lever, affectedCount })),
  };
}

export interface MetricConsumption {
  /** Documentos que a franquia dos usuários Base cobre no ano. */
  entitlement: number | undefined;
  /** Consumo ponderado medido, pelos pesos da SAP. */
  consumed: number | undefined;
  /** `entitlement − consumed`, nunca negativo: excedente vira bloco pago. */
  balance: number | undefined;
  /** Fração do licenciado que o consumo ocupa. */
  usedShare: number | undefined;
  /** `true` quando a contagem inclui documento derivado, e portanto é **teto**
   *  e não medição (#948).
   *
   *  Vem do cálculo, e não de uma regra guardada na tela: no dia em que a
   *  exclusão existir o backend manda `false` e o aviso some sozinho. Execução
   *  anterior à regra não traz a chave, e aí vale `true` — assumir o contrário
   *  apresentaria como medido um número que ninguém mediu. */
  countsDerivedDocuments: boolean;
  /** Por tipo de documento, com o peso que a SAP dá a cada um.
   *
   * O desenho quebra por **integração** e a medição não tem esse eixo: ninguém
   * extrai qual interface cria qual documento. O tipo é o que se mede. */
  byType: {
    type: string;
    count: number;
    weight: number;
    weighted: number;
    /** Quanto do licenciado este tipo ocupa, pelo **ponderado**: é o número
     * que soma com a barra. Pela contagem bruta a coluna somaria outra coisa
     * que não o total mostrado, e a tabela discordaria de si mesma. */
    share: number | undefined;
  }[];
  /** De onde a franquia veio, SKU a SKU. O desenho escreve
   * "FIN Base · 45 usuários × 20.000 docs" e não só a soma. */
  sources: {
    sku: string | undefined;
    description: string | undefined;
    users: number;
    docsPerUser: number;
    docs: number;
  }[];
  /** Estimativa e não medição no destino (#685). */
  isEstimate: boolean;
}

/**
 * Digital Access: o que a franquia cobre contra o que o ambiente consome (#682).
 *
 * `undefined` quando não há linha de Digital Access na execução — que é
 * diferente de consumo zero, e a tela precisa dizer qual das duas é.
 */
export function digitalAccessConsumption(
  sections: DiagnosticSections,
): MetricConsumption | undefined {
  const line = licenseLines(sections).find((l) => l.isDigitalAccess);
  if (!line) return undefined;

  const entitlement = line.entitlement;
  const consumed = line.totalWeighted;
  const balance =
    entitlement != null && consumed != null
      ? Math.max(entitlement - consumed, 0)
      : undefined;

  const rawLines = dataOf(sections, "bom_totals").lines;
  const rawLine = Array.isArray(rawLines)
    ? rawLines.find((l) => rec(l).is_digital_access === true)
    : undefined;
  // A ausência puxa para o lado que avisa: ver o comentário do campo.
  const countsDerivedDocuments =
    rec(rawLine).counts_derived_documents !== false;
  const byType: MetricConsumption["byType"] = [];
  for (const [type, value] of Object.entries(rec(rec(rawLine).breakdown))) {
    const entry = rec(value);
    const weighted = num(entry.weighted) ?? 0;
    byType.push({
      type,
      count: num(entry.count) ?? 0,
      weight: num(entry.weight) ?? 1,
      weighted,
      share:
        entitlement != null && entitlement > 0
          ? weighted / entitlement
          : undefined,
    });
  }
  byType.sort((a, b) => b.weighted - a.weighted);

  const sources: MetricConsumption["sources"] = [];
  const rawSources = rec(rawLine).entitlement_sources;
  if (Array.isArray(rawSources)) {
    for (const value of rawSources) {
      const entry = rec(value);
      sources.push({
        sku: text(entry.sku_number),
        description: text(entry.description),
        users: num(entry.users) ?? 0,
        docsPerUser: num(entry.docs_per_user) ?? 0,
        docs: num(entry.docs) ?? 0,
      });
    }
  }
  sources.sort((a, b) => b.docs - a.docs);

  return {
    entitlement,
    consumed,
    balance,
    usedShare:
      entitlement != null && entitlement > 0 && consumed != null
        ? consumed / entitlement
        : undefined,
    byType,
    sources,
    isEstimate: line.basis === "estimate",
    countsDerivedDocuments,
  };
}

export interface BomReviewCaveat {
  /** `bom_status` cru, quando veio. Serve para a copy distinguir os motivos. */
  status?: string;
  /** O booleano que o nó 05 grava, quando veio. */
  requiresReview: boolean;
}

/**
 * A ressalva do BOM, para ser dita junto do número (#753).
 *
 * Do plano de pré-venda: "um BOM que precisa de revisão tem exatamente a mesma
 * aparência de um aprovado... aparece como número redondo, que é o modo de
 * falha mais caro dos três". Medido em produção em 03/09/2026: uma execução
 * ficou em `needs_review` sem nenhuma diferença visual.
 *
 * `undefined` cobre dois casos que a tela trata igual e a origem distingue:
 * aprovado de fato, e sem informação. **Ausência de dado não vira selo de
 * pendência** — alarme falso numa apresentação custa tanto quanto silêncio.
 */
export function bomReviewCaveat(
  sections: DiagnosticSections,
): BomReviewCaveat | undefined {
  const data = dataOf(sections, "bom_totals");
  const status = text(data.bom_status);
  const requiresReview = data.requires_review === true;

  if (!requiresReview && (status == null || status === "approved"))
    return undefined;
  return { status: status ?? undefined, requiresReview };
}

export interface WricefEffortRow {
  /** Letra do acrônimo, ou `"?"` — o bucket que `effort_estimator.py:336` emite
   * para objeto sem `wricef_type` (Correção 9). Descartá-lo faria a soma das
   * barras deixar de fechar contra `total_effort_days`. */
  type: string;
  days: number;
}

/** As barras de esforço por tipo WRICEF, da maior para a menor. */
export function wricefEffortRows(
  sections: DiagnosticSections,
): WricefEffortRow[] | undefined {
  if (isUnknown(envelopeOf(sections, "cost_breakdown"))) return undefined;

  const byType = countMap(dataOf(sections, "cost_breakdown").by_wricef_type);
  return Object.entries(byType)
    .map(([type, days]) => ({ type, days }))
    .sort((a, b) => b.days - a.days);
}

export interface ProfileSeniorityRow {
  key: string;
  days: number;
  /** `undefined` quando `hasRate === false` — a célula não pode dizer "R$ 0.00". */
  rateBrlDay: number | undefined;
  subtotal: number | undefined;
  isPlaceholder: boolean;
  hasRate: boolean;
}

/**
 * A tabela perfil × senioridade, com a marcação de `placeholder_profiles` e a
 * distinção sem-rate/rate-provisório carregada célula a célula.
 */
export function profileSeniorityRows(
  sections: DiagnosticSections,
): ProfileSeniorityRow[] {
  if (isUnknown(envelopeOf(sections, "cost_breakdown"))) return [];

  const data = dataOf(sections, "cost_breakdown");
  const placeholders = new Set(strings(data.placeholder_profiles));

  return Object.entries(rec(data.by_profile_seniority)).map(([key, raw]) => {
    const entry = rec(raw);
    const rate = num(entry.rate_brl_day);
    const hasRate = rate != null && rate > 0;
    return {
      key,
      days: num(entry.days) ?? 0,
      rateBrlDay: hasRate ? rate : undefined,
      subtotal: hasRate ? num(entry.subtotal) : undefined,
      isPlaceholder: placeholders.has(key),
      hasRate,
    };
  });
}

export interface CostTotals {
  effortDays: number | undefined;
  subtotal: number | undefined;
  overhead: number | undefined;
  overheadMultiplier: number | undefined;
  total: number | undefined;
}

/** O rodapé de `cost_breakdown` — leitura pura, nenhuma conta refeita. */
export function costTotals(
  sections: DiagnosticSections,
): CostTotals | undefined {
  if (isUnknown(envelopeOf(sections, "cost_breakdown"))) return undefined;

  const data = dataOf(sections, "cost_breakdown");
  return {
    effortDays: num(data.total_effort_days),
    subtotal: num(data.subtotal_brl),
    overhead: num(data.overhead_brl),
    overheadMultiplier: num(data.overhead_multiplier),
    total: num(data.total_brl),
  };
}

export interface LicenseLine {
  skuNumber: string | undefined;
  description: string | undefined;
  userCount: number | undefined;
  /** A métrica da linha. A quantidade só é gente quando ela é usuário: o group
   * reporting entra com 1 e métrica `Object`. `undefined` em payload anterior
   * ao campo, que segue contado como usuário, como o nó 05 faz. */
  metric: string | undefined;
  effectiveCount: number | undefined;
  minBlocks: number | undefined;
  unitPriceBrl: number | undefined;
  monthlyTotal: number | undefined;
  annualTotal: number | undefined;
  isDigitalAccess: boolean;
  /**
   * Por que a linha não tem preço: `on_request` é a SAP remetendo à lista
   * completa (pedir cotação), `unknown` é lacuna da nossa referência
   * (semear). As duas somam zero ao total e pedem ações opostas (#668).
   */
  priceStatus: string | undefined;
  /**
   * A procedência do número (#685). `estimate` com origem ECC, onde a SAP
   * classifica a leitura pré-migração como não relevante para auditoria.
   */
  basis: string | undefined;
  /**
   * De que ano a estimativa saiu, e se esse ano é o último completo (#944).
   * Quando o último ano completo não tem documento, a extração recua até o
   * último com fluxo — e a tela precisa dizer isso, porque "2018" sozinho não
   * distingue janela antiga de ano corrente vazio.
   */
  basisYear: string | undefined;
  basisYearIsFallback: boolean;
  lastCompleteYear: string | undefined;
  /**
   * A franquia anual de documentos dos usuários qualificados e o consumo
   * ponderado do ano (#937). Zero bloco tem duas causas, e só estes dois
   * separam "não consome" de "consome dentro da franquia".
   */
  entitlement: number | undefined;
  totalWeighted: number | undefined;
}

/**
 * A amostra de linhas de licença (`bom_totals.lines`, capada em 200).
 *
 * `unitPriceBrl`/`monthlyTotal`/`annualTotal` ficam `undefined` quando nulos —
 * a linha de Digital Access não semeada no catálogo, ou qualquer SKU sem
 * `list_price_brl`, nunca vira `R$ 0.00`.
 */
export function licenseLines(sections: DiagnosticSections): LicenseLine[] {
  if (isUnknown(envelopeOf(sections, "bom_totals"))) return [];

  return list(dataOf(sections, "bom_totals").lines).map((row) => {
    const entry = rec(row);
    return {
      skuNumber: text(entry.sku_number),
      description: text(entry.description),
      userCount: num(entry.user_count),
      metric: text(entry.metric),
      effectiveCount: num(entry.effective_count),
      minBlocks: num(entry.min_blocks),
      unitPriceBrl: num(entry.unit_price_brl),
      monthlyTotal: num(entry.monthly_total),
      annualTotal: num(entry.annual_total),
      isDigitalAccess: entry.is_digital_access === true,
      /** `listed` | `on_request` | `unknown`. Separa "a SAP remete à lista
       * completa de preços" de "falta na nossa referência" — ações opostas
       * para quem lê (#668). */
      priceStatus: text(entry.price_status),
      basis: text(entry.basis),
      basisYear: text(entry.basis_year),
      basisYearIsFallback: entry.basis_year_is_fallback === true,
      lastCompleteYear: text(entry.last_complete_year),
      entitlement: num(entry.entitlement),
      totalWeighted: num(entry.total_weighted),
    };
  });
}

/** A quantidade da linha é contagem de gente: métrica de usuário, ou ausente. */
export function countsPeople(line: Pick<LicenseLine, "metric">): boolean {
  return line.metric == null || line.metric.trim().toLowerCase() === "user";
}

/**
 * Os Developer cobertos pela franquia do SAP Finance Base, ou `undefined`.
 *
 * O nó 05 tira essa linha da BOM e os usuários seguem ativos; a api deriva
 * quantos são (`developer_allowance` em `user_sku_sample`).
 */
export function developerAllowance(
  sections: DiagnosticSections,
): { sku: string; users: number } | undefined {
  if (isUnknown(envelopeOf(sections, "user_sku_sample"))) return undefined;
  const entry = rec(dataOf(sections, "user_sku_sample").developer_allowance);
  const sku = text(entry.sku_number);
  const users = num(entry.users);
  return sku && users != null && users > 0 ? { sku, users } : undefined;
}

export interface LicenseTotals {
  /** Do **agregado** — já é a soma das linhas com preço, nunca recalculado da
   * amostra (Correção 7). */
  monthly: number | undefined;
  annual: number | undefined;
  /** Quantas linhas não somam ao total.
   *
   * Vem do **agregado** (`unpriced_count`), contado pelo nó 05 sobre a lista
   * inteira e com o filtro de quantidade faturável — `0 × preço desconhecido`
   * é zero e não subestima nada. A contagem da amostra fica como retaguarda
   * para payload antigo: ela vê no máximo 200 linhas e não tem esse filtro,
   * então dizia um número diferente do que o BOM afirma (#668). */
  unpricedCount: number;
  /** Dessas, quantas faltam na NOSSA referência (`price_status: "unknown"`).
   *
   * A copy do tile manda "cotar", que é a ação do `on_request` — a SAP
   * remetendo à lista completa de preços. Para uma lacuna de seed a ação é
   * outra, e mandar cotar desperdiça a ida ao cliente (#668).
   *
   * Contado sobre a **amostra**, porque o motivo viaja na linha e o agregado
   * só traz o total. Linha sem `price_status` é payload anterior ao campo e
   * **não** conta: sem saber o motivo, não há por que trocar a copy. */
  unpricedUnknownCount: number;
  shownCount: number;
}

/** O rodapé da tabela de licenças: soma só o que tem preço e declara o resto. */
export function licenseTotals(
  sections: DiagnosticSections,
): LicenseTotals | undefined {
  if (isUnknown(envelopeOf(sections, "bom_totals"))) return undefined;

  const data = dataOf(sections, "bom_totals");
  const lines = licenseLines(sections);
  return {
    monthly: num(data.monthly_grand_total),
    annual: num(data.annual_grand_total),
    unpricedCount:
      num(data.unpriced_count) ??
      lines.filter((line) => line.unitPriceBrl == null).length,
    unpricedUnknownCount: lines.filter(
      (line) => line.unitPriceBrl == null && line.priceStatus === "unknown",
    ).length,
    shownCount: lines.length,
  };
}

export interface OutOfScopeExcluded {
  outOfScope: number;
  total: number;
}

/**
 * Quantos objetos do catálogo estão `out_of_scope` **hoje** — `wricef_catalog`,
 * não `cost_breakdown`: os dois podem ser de instantes diferentes (um é tabela
 * de projeto, o outro é snapshot do Architect), então a ressalva fala do
 * catálogo agora, não afirma que são os mesmos N que a run excluiu do esforço.
 */
export function outOfScopeExcluded(
  sections: DiagnosticSections,
): OutOfScopeExcluded | undefined {
  if (isUnknown(envelopeOf(sections, "wricef_catalog"))) return undefined;

  const byScopeStatus = countMap(
    dataOf(sections, "wricef_catalog").by_scope_status,
  );
  const outOfScope = byScopeStatus.out_of_scope;
  if (outOfScope == null) return undefined;

  const total = Object.values(byScopeStatus).reduce((acc, n) => acc + n, 0);
  return { outOfScope, total };
}

// ---------------------------------------------------------------------------
// Ato 2 (#879): os cards do desenho aprovado cuja fonte já existe no payload.
// ---------------------------------------------------------------------------

export interface ComplexityHighlights {
  recommendedScopeItems?: number;
  gapsToDecide?: number;
  objectsNeedingEngineering?: number;
  /** Denominador da frase "dos N, só estes exigem trabalho técnico". */
  catalogTotal?: number;
}

/**
 * Os três indicadores do topo do Ato 2.
 *
 * `objectsNeedingEngineering` é a **soma dos alvos de extensão**, e não o
 * catálogo inteiro. O desenho diz "dos 3.267, só estes exigem trabalho
 * técnico": somar o catálogo diria que todo objeto Z exige engenharia, que é
 * exatamente a leitura que o Ato 2 existe para desfazer.
 *
 * Ausência devolve `undefined`, nunca zero. Seção que não chegou e catálogo com
 * zero objeto são coisas diferentes, e a tela precisa das duas.
 */
export function complexityHighlights(
  sections: DiagnosticSections,
): ComplexityHighlights {
  const gaps = isUnknown(envelopeOf(sections, "scope_gaps"))
    ? {}
    : rec(dataOf(sections, "scope_gaps").counts);
  const catalog = isUnknown(envelopeOf(sections, "wricef_catalog"))
    ? {}
    : dataOf(sections, "wricef_catalog");

  // Lê o **cruzamento** `by_disposition_target`, nunca a marginal
  // `by_extensibility_target`, pelo mesmo motivo do `extensionTargets` do Ato 1:
  // a marginal agrega o catálogo inteiro e traz alvo de objeto que nem pertence
  // ao balde de extensão.
  //
  // Medido em 23/09/2026 no corpus de 7.593 objetos: a marginal somava 2.738 e
  // o cruzamento soma 2.561. Os 177 de diferença são 170 `undecided` e 7
  // `retire` — objeto que ninguém decidiu ainda e objeto marcado para
  // descontinuar, contados como "precisa de trabalho técnico".
  const targets = countMap(
    rec(dataOf(sections, "wricef_catalog").by_disposition_target)
      .requires_extension,
  );
  // `_UNKNOWN` é objeto que exige extensão e ainda não tem alvo decidido. Fica
  // fora porque alvo indefinido não é promessa de engenharia, e contá-lo
  // inflaria o número que a tela apresenta como "só estes".
  const engineering = Object.entries(targets)
    .filter(([key]) => key !== "_UNKNOWN")
    .reduce((acc, [, value]) => acc + value, 0);

  // Sem aprovação, os gaps são recortados contra um conjunto vazio e a
  // contagem sai zero. Zero ali afirma "nada ficou fora do padrão", que é
  // conclusão; o certo é "ainda não se sabe", igual ao indicador ao lado.
  const gapsMeasured =
    isUnknown(envelopeOf(sections, "scope_gaps")) ||
    hasActiveScopeApproval(sections, "scope_gaps");

  return {
    recommendedScopeItems: num(approvedScope(sections).in_scope),
    gapsToDecide: gapsMeasured ? num(gaps.coverage_gaps) : undefined,
    objectsNeedingEngineering: Object.keys(targets).length
      ? engineering
      : undefined,
    catalogTotal: num(catalog.total),
  };
}

/**
 * As contagens da foto de aprovação do Fit-to-Standard.
 *
 * É a população dos indicadores do Ato 2, e **não** `summary`, que é o run
 * bruto do motor ECC. Medido em 23/09/2026 no projeto `Demo apresentacao
 * [18/09]`: o motor avaliou 614 scope items e o analista deixou 54 em
 * `activate` mais 262 em `decision`. O card mostrava 614, que conta também os
 * 298 que ele tirou do escopo.
 *
 * Bloco ausente devolve `{}`, e aí cada indicador sai `undefined`: antes de
 * aprovar, a única coisa que existe é candidato do motor, e mostrá-lo como
 * "recomendado" seria apresentar um número que ninguém revisou.
 */
function approvedScope(sections: DiagnosticSections): Record<string, unknown> {
  if (isUnknown(envelopeOf(sections, "scope_activation"))) return {};
  return rec(dataOf(sections, "scope_activation").approved);
}

export interface ModuleCustomizationRow {
  module: string;
  count: number;
  /**
   * O `module` é um nome de verdade, ou o código cru?
   *
   * Cair no código é deliberado (`application_component_labels.py`: *"balde
   * com contagem e sem nome é pior que balde nomeado pelo código"*), mas o
   * código ao lado de dois nomes deixa de se ler como código. Medido em
   * 02/10/2026: a barra do meio do card se chamava **"X"**, entre "Válido para
   * várias aplicações" e "Contabilidade financeira".
   *
   * Quem diz isso ao leitor é a tela, com i18n — este arquivo só marca.
   */
  labelled: boolean;
  /** O código do componente de aplicação. É ele que filtra o detalhe (#1477). */
  code: string;
}

export interface ModuleCustomizations {
  /** Só módulos **nomeados**. O balde sem módulo sai em `unidentified`. */
  rows: ModuleCustomizationRow[];
  /** A cauda somada. Zero quer dizer "não há cauda", não "escondi algo". */
  remainder: number;
  /** Objetos sem módulo atribuível. Zero quer dizer "todos têm". */
  unidentified: number;
  /** O que as barras repartem: os nomeados mais a cauda. */
  identified: number;
  /** O catálogo inteiro, identificado ou não. */
  total?: number;
}

/**
 * Onde o código Z se concentra, por módulo.
 *
 * O eixo é **`by_stream`**, e não `by_module` (#906). Medido em 23/09/2026 no
 * corpus de 7.593 objetos: `module` é a LoB do modelo por objeto, que a passada
 * leve não roda, enquanto `stream` vem do `APPL` do TRDIR pela taxonomia da
 * #297 e traz doze baldes com nome de negócio. Desde 30/09/2026 a passada leve
 * deixa `module` vazio em vez de estampar `CROSS` em todos.
 *
 * O card lia a coluna colapsada tendo a boa ao lado.
 *
 * A cauda é **somada**, não descartada: listar quarenta módulos num card de
 * apresentação é ruído, e esconder a cauda sem somá-la faria as barras não
 * fecharem com o total que o próprio card declara.
 */
export function moduleCustomizations(
  sections: DiagnosticSections,
  options: { top?: number } = {},
): ModuleCustomizations {
  if (isUnknown(envelopeOf(sections, "wricef_catalog"))) {
    return { rows: [], remainder: 0, unidentified: 0, identified: 0 };
  }

  const data = dataOf(sections, "wricef_catalog");
  const counts = countMap(data.by_stream);
  // #906: a contagem é chaveada pelo **código** do componente de aplicação, e
  // o texto vem ao lado, no idioma de quem lê. O snapshot congela o eixo, e
  // não a frase: congelar a frase faria uma calibragem publicada em português
  // continuar em português para um leitor em inglês, para sempre.
  //
  // Código fora do mapa aparece como o próprio código. É melhor que uma barra
  // com número e sem nome, e o `appl` fora das 24 letras da TAPLT existe.
  const labels = (data.stream_labels ?? {}) as Record<string, string>;
  const label = (code: string) => labels[code] ?? code;
  const labelled = (code: string) => typeof labels[code] === "string";
  // O balde sem módulo **não é barra**. Ele já não disputava o topo nem a
  // cauda; desde 29/09/2026 ele também não disputa a régua.
  //
  // Três posições tentadas, nesta ordem, e o que cada uma quebrava:
  //
  // 1. dentro da cauda — sumia. Medido em 23/09, "Demais módulos: 5.327", dos
  //    quais 4.908 eram ele, e quem lê "demais" supõe módulos pequenos;
  // 2. linha própria no fim — visível, mas é a MAIOR barra do card (3.939
  //    contra 2.233 do maior módulo nomeado). Num card que o cliente lê, a
  //    resposta mais alta para "onde o código Z se concentra" passava a ser
  //    "não sabemos";
  // 3. nota informativa, com o número e a fração — é o que está aqui.
  //
  // Some da régua, não do card: as barras passam a repartir o identificado, e
  // o não identificado é dito por extenso logo abaixo. A frase de concentração
  // já usava esse denominador desde a #963, então o card era incoerente com
  // ele mesmo enquanto o balde era barra.
  const unknown = counts[UNKNOWN_BUCKET] ?? 0;
  // O desempate é pelo **texto**, e não pelo código: a ordem que a pessoa vê
  // tem de ser a que ela leria, e duas barras de mesma contagem em ordem de
  // código pareceriam embaralhadas na tela.
  const named = Object.entries(counts)
    .filter(([module]) => module !== UNKNOWN_BUCKET)
    .sort((a, b) => b[1] - a[1] || label(a[0]).localeCompare(label(b[0])));
  const top = options.top ?? named.length;
  const rows = named.slice(0, top).map(([module, count]) => ({
    module: label(module),
    code: module,
    count,
    labelled: labelled(module),
  }));

  // A cauda são os módulos nomeados que não couberam, e só eles.
  const remainder = named.slice(top).reduce((acc, [, count]) => acc + count, 0);
  return {
    rows,
    remainder,
    unidentified: unknown,
    // O denominador das barras. Repartir o catálogo inteiro faria as barras
    // não fecharem com o total declarado no instante em que o balde sem
    // módulo saiu delas.
    identified: rows.reduce((acc, row) => acc + row.count, 0) + remainder,
    total: num(data.total),
  };
}

/** O balde de quem não tem módulo atribuível, como o filtro do catálogo o espera. */
export const UNASSIGNED_MODULE = UNKNOWN_BUCKET;

export interface ModuleBucket {
  code: string;
  label: string;
  count: number;
  labelled: boolean;
}

/**
 * Todos os módulos do card, sem o corte do topo, para o filtro do detalhe
 * "Customizações por módulo". A ordem e o rótulo são os do card; o balde sem
 * módulo atribuível sai à parte, como no card.
 */
export function moduleBuckets(sections: DiagnosticSections): {
  buckets: ModuleBucket[];
  unidentified: number;
  identified: number;
} {
  const { rows, unidentified, identified } = moduleCustomizations(sections);
  return {
    buckets: rows.map((row) => ({
      code: row.code,
      label: row.module,
      count: row.count,
      labelled: row.labelled,
    })),
    unidentified,
    identified,
  };
}

export interface DestinationSegment {
  key: string;
  count: number;
  /** Só existe quando há denominador declarado. */
  pct?: number;
}

export interface Destination {
  segments: DestinationSegment[];
  total?: number;
}

/**
 * Percentuais inteiros que somam o que a soma exata pede (100, num universo
 * fechado). Arredondar cada fatia sozinha deixa 99 ou 101 numa barra de três
 * terços; aqui cada uma fica no piso e o que sobra vai, uma a uma, às de maior
 * resto, na ordem em que vieram.
 *
 * O resto é comparado como `contagem * 100 - piso * total`, e não como fração:
 * 52/68 e 1/68 empatam em 32/68, mas em ponto flutuante saem 0,470588235294116 e
 * 0,47058823529411775, e o desempate passava a depender do erro de arredondamento.
 * Contagens fracionárias (horas) empatam dentro de uma tolerância.
 */
export function closedPercents(counts: readonly number[], total: number): number[] {
  const EPSILON = 1e-9 * total;
  const floors = counts.map((count) => Math.floor((count * 100) / total + 1e-9));
  const remainders = counts.map((count, index) => count * 100 - floors[index] * total);
  let missing =
    Math.round((counts.reduce((sum, count) => sum + count, 0) / total) * 100) -
    floors.reduce((sum, floor) => sum + floor, 0);
  const byRemainder = remainders
    .map((remainder, index) => ({ index, remainder }))
    .sort((a, b) =>
      Math.abs(a.remainder - b.remainder) <= EPSILON ? a.index - b.index : b.remainder - a.remainder,
    );
  for (const { index } of byRemainder) {
    if (missing <= 0) break;
    floors[index] += 1;
    missing -= 1;
  }
  return floors;
}

/**
 * Percentual só com denominador, que é critério da issue.
 *
 * Sem total, o segmento sai com a contagem e sem `pct`. Derivar o denominador
 * da soma dos segmentos seria inventar um todo que ninguém declarou, e o card
 * passaria a afirmar cobertura que o dado não sustenta.
 */
function toDestination(
  counts: Record<string, number>,
  total?: number,
): Destination {
  const segments = Object.entries(counts).map(([key, count]) => ({
    key,
    count,
    pct: total ? Math.round((count / total) * 100) : undefined,
  }));
  return { segments, total };
}

/** Para onde vai cada objeto customizado sob Clean Core. */
export function cleanCoreDestination(
  sections: DiagnosticSections,
): Destination {
  if (isUnknown(envelopeOf(sections, "wricef_catalog")))
    return { segments: [] };

  const data = dataOf(sections, "wricef_catalog");
  return toDestination(countMap(data.by_disposition), num(data.total));
}

/**
 * Destino provável dos processos, em barra única.
 *
 * Divide o escopo **aprovado** em duas fatias: o que já tem caminho
 * (`activate`) e o que ainda depende de decisão (`decision`). É a mesma
 * população do indicador "scope items recomendados", e é isso que faz os dois
 * números da tela fecharem um com o outro.
 */
export function processDestination(sections: DiagnosticSections): Destination {
  if (isUnknown(envelopeOf(sections, "scope_activation")))
    return { segments: [] };

  // A barra divide a MESMA população do indicador de cima, o escopo aprovado.
  // Saindo do `summary` do run, a tela mostrava 316 no topo e desenhava sobre
  // 614 na dobra seguinte, com os dois números à vista.
  //
  // `out_of_scope` fica fora das fatias de propósito: desenhá-lo como destino
  // diria que o item tem um caminho no projeto, quando ele não está no projeto.
  const approved = approvedScope(sections);
  const counts: Record<string, number> = {};
  for (const key of ["activate", "decision"]) {
    const value = num(approved[key]);
    if (value !== undefined) counts[key] = value;
  }
  return toDestination(counts, num(approved.in_scope));
}

export interface GapCategoryRow {
  coverageType: string;
  count: number;
}

export interface GapCategories {
  rows: GapCategoryRow[];
  /** A população inteira de gaps, de `counts.coverage_gaps`. */
  total: number | undefined;
}

/**
 * Gaps contados por categoria de cobertura (#325).
 *
 * O eixo é `by_coverage_type`, e a escolha não é estética: ele vem **agregado
 * sobre a população inteira** pelo builder. Contar por `lob` sairia da lista
 * `coverage_gaps`, que é amostra capada em 100, e subnotificaria em silêncio
 * num cliente com muitos gaps — o tipo de erro que some no teste e aparece na
 * reunião.
 *
 * É este card que o Ato 2 tem, e não a lista: a nota do apresentador do ato diz
 * "quantidades e percentuais sim; a lista dos 43 gaps, não. Nada de valores".
 */
export function gapCategories(sections: DiagnosticSections): GapCategories {
  if (isUnknown(envelopeOf(sections, "scope_gaps")))
    return { rows: [], total: undefined };

  const data = dataOf(sections, "scope_gaps");
  const counts = countMap(data.by_coverage_type);
  const rows = Object.entries(counts)
    .map(([coverageType, count]) => ({ coverageType, count }))
    .sort(
      (a, b) =>
        b.count - a.count || a.coverageType.localeCompare(b.coverageType),
    );

  return { rows, total: num(rec(data.counts).coverage_gaps) };
}

export interface ProcessFitRow {
  process: string;
  total: number;
  /** Itens em Ativar no escopo aprovado. */
  activate: number;
  /** Itens ainda em Decisão no escopo aprovado. */
  decision: number;
  /** Só existe quando o processo declara o próprio total. */
  fitPct?: number;
}

/**
 * O eixo guardado pela leitura do motor ECC, e não pelo veredito.
 *
 * Até 21/10/2026 o backend contava `needs_decision`, `needs_review` e `skip`
 * por processo; a Calibragem publicada antes disso guarda essa foto. Ela não se
 * converte no veredito sem os itens, então a tela não a desenha e pede a
 * Calibragem publicada de novo.
 */
function legacyProcessAxis(byProcess: Record<string, unknown>): boolean {
  return Object.values(byProcess).some((raw) => !("decision" in rec(raw)));
}

/**
 * Aderência Fit-to-Standard por processo end-to-end (#879).
 *
 * O eixo vem da curadoria em `ref_business_area_process`, resolvida no backend:
 * nenhuma tabela do schema tinha processo, e o catálogo só oferece linha de
 * negócio, área de negócio e cluster.
 *
 * `other` entra na ordem como qualquer outra linha. Ele teve lugar reservado no
 * fim, e com isso "Indústria e transversais" com 82% caía depois de um 51%, que
 * é a desordem que a ordenação por aderência existe para tirar. O desenho não
 * abre exceção: a quinta linha dele também não é processo, e está por último
 * porque tem o menor percentual.
 *
 * As colunas são o veredito do escopo aprovado, Ativar e Decisão, e não a
 * leitura do motor ECC (21/10/2026): o card pintava "Precisa de decisão" itens
 * que o analista já tinha ativado. O percentual é o do Fit-to-Standard.
 *
 * Seção sem o eixo devolve lista vazia, e não baldes zerados. Run anterior à
 * curadoria não tem `by_process`, e quatro zeros diriam "nenhum processo
 * aderente", que é afirmação e não ausência. A foto contada pela leitura do ECC
 * também volta vazia (ver `processFitNeedsRepublish`).
 */
export function processFitRows(sections: DiagnosticSections): ProcessFitRow[] {
  if (isUnknown(envelopeOf(sections, "scope_activation"))) return [];

  const byProcess = rec(dataOf(sections, "scope_activation").by_process);
  if (legacyProcessAxis(byProcess)) return [];
  return Object.entries(byProcess)
    .map(([process, raw]) => {
      const counts = countMap(raw);
      const total = counts.total ?? 0;
      return {
        process,
        total,
        activate: counts.activate ?? 0,
        decision: counts.decision ?? 0,
        fitPct: total
          ? Math.round(((counts.activate ?? 0) / total) * 100)
          : undefined,
      };
    })
    .sort((a, b) => {
      // Estritamente pela **aderência**, e não pelo volume de scope items
      // (#325). O card se chama "Aderência por processo" e o percentual é o
      // assunto dele; por volume a coluna saía 51%, 96%, 90%, que lê como
      // desordem e perde a leitura do desenho, de onde o padrão cobre para
      // onde ele não cobre.
      //
      // A sobra **não** tem lugar reservado no fim. Ela chegou a ter, e com
      // isso "Indústria e transversais" com 82% caía depois de um 51% — a
      // mesma desordem por outra porta. O desenho não abre exceção: a quinta
      // linha dele é "Localização fiscal BR", que também não é processo, e
      // está por último porque tem o menor percentual.
      //
      // Só quem não tem percentual vai para o fim: sem denominador não há
      // posição, e inventar uma seria ordenar por um número que não existe.
      if (a.fitPct === undefined) return 1;
      if (b.fitPct === undefined) return -1;
      return b.fitPct - a.fitPct || a.process.localeCompare(b.process);
    });
}

/**
 * O eixo existe, mas foi guardado pela leitura do motor ECC.
 *
 * É o caso da Calibragem publicada antes de 21/10/2026. O card diz que a foto
 * precisa ser publicada de novo, em vez da mensagem de run antiga, que mandaria
 * rodar o agente quando o que falta é republicar.
 */
export function processFitNeedsRepublish(sections: DiagnosticSections): boolean {
  if (isUnknown(envelopeOf(sections, "scope_activation"))) return false;
  const byProcess = rec(dataOf(sections, "scope_activation").by_process);
  return Object.keys(byProcess).length > 0 && legacyProcessAxis(byProcess);
}

// --------------------------------------------------------------------------
// o que migra de dados (Ato 4)
// --------------------------------------------------------------------------

/**
 * As dez tabelas que o connector conta, classificadas em mestre e documento.
 *
 * **É curadoria SAP, não dado.** A regra que ela carrega é a do SAP Migration
 * Cockpit: objeto mestre vai para o S/4, histórico transacional fica no legado.
 * Cliente e fornecedor entram juntos porque no S/4 os dois viram Business
 * Partner, e separá-los na tela mostraria uma distinção que o destino não tem.
 *
 * Mora aqui, e não numa `ref_*`, porque a lista de tabelas que o connector
 * varre também é fixa no código (`_VOLUME_TABLES`, em `rfc.py`). Se uma das
 * duas virar configurável, as duas mudam de lugar juntas.
 *
 * Tabela que o connector ganhar e esta lista não conhecer fica **fora dos dois
 * lados**: classificá-la por omissão poria um número no lado errado.
 */
const MIGRATION_MASTER: ReadonlyArray<{
  key: string;
  tables: readonly string[];
}> = [
  { key: "materials", tables: ["MARA"] },
  { key: "business_partners", tables: ["KNA1", "LFA1"] },
  { key: "gl_accounts", tables: ["SKA1"] },
  { key: "cost_centers", tables: ["CSKS"] },
];

/** O histórico que não atravessa: documento, não objeto. */
const MIGRATION_LEGACY: readonly string[] = [
  "BKPF",
  "MKPF",
  "EKKO",
  "LIKP",
  "VBAK",
];

export interface MigrationLine {
  key: string;
  count: number;
  /** As tabelas somadas, para o card declarar a origem de cada linha. */
  tables: string[];
}

/**
 * Documento em aberto, o recorte que atravessa junto com o dado mestre.
 *
 * `undefined` em cada campo é "não medido", e nunca zero: zero diria que não há
 * pedido em aberto, que é o contrário do que uma coleta falha significa.
 */
export interface OpenDocuments {
  sales?: number;
  purchases?: number;
  /** Alguma das duas bateu no teto de leitura: o número é piso, não exato. */
  isFloor: boolean;
}

export interface MigrationScope {
  master: MigrationLine[];
  masterTotal: number;
  legacyTotal: number;
  /** Alguma tabela somada bateu no teto de leitura: os totais são piso. */
  isFloor: boolean;
  /** Ausente nas execuções anteriores à #880, que não coletavam o recorte. */
  open?: OpenDocuments;
}

/**
 * As duas contagens de documento em aberto, quando a execução as coletou.
 *
 * Bloco presente e sem nenhuma contagem devolve `undefined`: o connector
 * respondeu e não mediu nada, o que para o card é o mesmo que não ter tentado.
 */
function openDocuments(
  block: Record<string, unknown>,
): OpenDocuments | undefined {
  const raw = block.open_documents;
  if (raw === undefined || raw === null) return undefined;

  const open = rec(raw);
  const sales = num(open.sales_orders_open);
  const purchases = num(open.purchase_orders_open);
  if (sales === undefined && purchases === undefined) return undefined;

  return { sales, purchases, isFloor: list(open.truncated).length > 0 };
}

/**
 * O que sai do legado para o S/4, e o que fica.
 *
 * Lê `data_volumes`, que já existia no payload e nenhum ato lia: ela perdeu o
 * card na #852 porque o Ato 1 pede volumetria por processo e por mês, que é
 * outra medição. A pergunta do Ato 4 é a que essas dez tabelas respondem.
 *
 * Tabela com `counted: false` fica de fora das duas somas. O connector não
 * conseguiu contá-la, e somá-la como zero diria que ela está vazia.
 */
export function migrationScope(
  sections: DiagnosticSections,
  /**
   * Quantas contas tem o plano de contas do cliente, da análise do plano de
   * contas (#1374). A SKA1 traz todos os planos do sistema, inclusive os
   * modelos da SAP, e na AL5 dava 20.642 contas contra 1.116 do plano AL5B.
   */
  client: { glAccounts?: number } = {},
): MigrationScope | undefined {
  if (isUnknown(envelopeOf(sections, "data_volumes"))) return undefined;

  const block = dataOf(sections, "data_volumes");
  const counts: Record<string, number> = {};
  const floor = new Set<string>();
  for (const entry of list(block.volumes)) {
    const row = rec(entry);
    const count = num(row.row_count);
    if (row.counted === true && count !== undefined) {
      const table = text(row.table) ?? "";
      counts[table] = count;
      // `truncated` é tabela que bateu no teto de varredura: o número é piso,
      // não medição. Somar sem dizer isso apresenta um piso como exato, que é
      // o mesmo erro que o card já evita nas linhas de documento em aberto.
      if (row.truncated === true) floor.add(table);
    }
  }

  // Seção coletada e nenhuma tabela contada não é "zero registros": é "não
  // deu para medir". O estado do envelope fica `ok` de propósito (o builder
  // conta `len(volumes)`, e dez tabelas tentadas é coleta), então quem
  // distingue é esta função. Sem isso o card afirma "0 registros mestres
  // migram" sobre uma run em que nada foi medido.
  if (Object.keys(counts).length === 0) return undefined;

  const master = MIGRATION_MASTER.map(({ key, tables }) => {
    const present = tables.filter((table) => table in counts);
    const measured = present.reduce((acc, table) => acc + counts[table], 0);
    return {
      key,
      tables: present,
      count:
        key === "gl_accounts" &&
        client.glAccounts !== undefined &&
        present.length > 0
          ? client.glAccounts
          : measured,
    };
  })
    .filter((line) => line.tables.length > 0)
    .sort((a, b) => b.count - a.count);

  const masterTotal = master.reduce((acc, line) => acc + line.count, 0);
  const legacyTotal = MIGRATION_LEGACY.reduce(
    (acc, table) => acc + (counts[table] ?? 0),
    0,
  );
  const summed = [
    ...master.flatMap((line) => line.tables),
    ...MIGRATION_LEGACY,
  ];
  const isFloor = summed.some((table) => floor.has(table));

  return {
    master,
    masterTotal,
    legacyTotal,
    isFloor,
    open: openDocuments(block),
  };
}

/** De onde veio o número que o Ato 5 mostra (#877).
 *
 * - `calibrated`: uma pessoa passou pela Calibragem e publicou.
 * - `approved`: veio da versão de escopo que o consultor congelou no
 *   Fit-to-Standard, que é o que será habilitado no Public.
 * - `machine`: saiu dos agentes, sem ninguém tocar.
 * - `unknown`: não dá para saber, porque o snapshot não veio. Não é `machine`:
 *   afirmar "ninguém tocou" sem ter lido seria inventar.
 *
 * A ordem importa: calibragem publicada vence escopo aprovado, porque ela é
 * posterior e é exatamente o que foi apresentado ao cliente.
 */
export type ProvenanceKind = "calibrated" | "approved" | "machine" | "unknown";

export interface Provenance {
  kind: ProvenanceKind;
  /** Quem publicou a calibragem. Só existe em `calibrated`. */
  by: string | null;
  /** Quando: publicação, no caso calibrado; aprovação, no caso aprovado. */
  at: string | null;
}

export function numberProvenance(
  calibration: DiagnosticSnapshot["calibration"] | undefined,
  snapshots: DiagnosticSnapshots | undefined,
): Provenance {
  if (calibration) {
    return {
      kind: "calibrated",
      by: calibration.published_by ?? null,
      at: calibration.published_at ?? null,
    };
  }
  // Sem `snapshots` não dá para saber se houve aprovação, e "de máquina" seria
  // afirmar que ninguém tocou. Desconhecido não é medição.
  if (!snapshots) return { kind: "unknown", by: null, at: null };

  // Sem cast: `scope` é obrigatório no contrato e `approved_at` já vem tipado.
  // O cast anterior aceitava qualquer chave, então um rename no backend
  // deixaria o `tsc` verde e o selo cairia calado para "de máquina".
  const scope = snapshots.scope;
  if (scope?.version != null || scope?.approved_at) {
    // A data pode faltar sem a aprovação faltar: o selo não depende dela, do
    // mesmo jeito que `calibrado` não depende do `published_at`.
    return { kind: "approved", by: null, at: scope.approved_at ?? null };
  }
  return { kind: "machine", by: null, at: null };
}

/** As cinco fases do prazo, na ordem em que o projeto as vive.
 *
 * Estas cinco mais `duration_months`, `total_hh` e `subtotal_phases` são
 * exatamente o que o snapshot servido carrega. O par está em
 * `services/api/app/services/calibration_worker.py`, em
 * `_SERVED_NUMBER_KEYS`, com teste afirmando o conjunto: tirar um campo de lá
 * faz esta função cair em `missing: "phases"` para todo projeto calibrado.
 */
export const TIMELINE_PHASES = [
  "explore_hh",
  "config_hh",
  "test_hh",
  "deploy_hh",
  "hypercare_hh",
] as const;

/** O que sobra do total depois das cinco fases: OCM, PMO, adders e
 *  contingência.
 *
 *  Não é detalhe de apresentação, é o que impede a barra de mentir. O número
 *  grande do card é o PRAZO, e o prazo sai do `total_hh`; as cinco fases são
 *  só o `subtotal_phases`. No caso fechado da spec isso é 3.901 contra 9.130,
 *  ou seja **as fases são 43% do esforço que produziu os meses**. Repartir só
 *  entre elas faria Hypercare aparecer como 12% quando ele é 5% do projeto, e
 *  quem lesse a barra ao lado de "3,9 meses" concluiria 0,47 mês em vez de
 *  0,21. O cliente não teria como perceber: o total não aparece no Ato 5.
 */
export const TIMELINE_OVERHEAD = "overhead_hh";

export interface TimelinePhase {
  key: (typeof TIMELINE_PHASES)[number] | typeof TIMELINE_OVERHEAD;
  hours: number;
  /** Fatia do **total** do projeto, 0 a 1. Do total, e não do subtotal de
   *  fases: é o total que produz os meses do card. */
  share: number;
}

export interface ProjectTimeline {
  /** `null` quando a versão publicada não traz duração utilizável. Zero conta
   *  como ausente: o motor devolve `0.0` por fallback de divisão. */
  months: number | null;
  /** `null` quando a versão publicada não carrega as cinco fases nem o total.
   *  Ausente não é zero: particionar sobre o que sobrou inventaria medição.
   *  Quando não é `null`, traz as cinco fases **mais o overhead**, e as fatias
   *  somam 1 sobre o total do projeto. */
  phases: TimelinePhase[] | null;
  /** O denominador, para a tela poder dizê-lo em vez de deixar supor. */
  totalHours: number | null;
  /** Por que não há partição, quando `phases` é `null`.
   *
   *  A tela precisa disto para não diagnosticar errado: faltar o total não é
   *  a mesma coisa que faltar as fases, e dizer a mensagem errada manda o
   *  consultor procurar no lugar errado. Quem sabe o motivo é quem decidiu. */
  missing: "duration" | "phases" | "total" | "inconsistent" | null;
}

/** Tolerância na conferência contra o `subtotal_phases`, em horas.
 *
 * O payload guarda os dois, e eles vêm do mesmo motor. Divergirem quer dizer
 * que a soma das cinco não é o subtotal que o resto da conta usou, e aí a
 * partição estaria repartindo outra coisa. Um HH de folga cobre arredondamento
 * de serialização sem deixar passar fase faltando.
 */
const SUBTOTAL_TOLERANCE_HH = 1;

/** O prazo e sua partição, ou `null` quando ninguém calibrou.
 *
 * `null` é ausência declarada, e não zero: o prazo só existe quando alguém
 * passou pela Calibragem, porque é o motor de esforço que o calcula. Mostrar
 * "0 meses" seria inventar medição.
 *
 * ## Por que `phases` pode ser `null` com `months` preenchido
 *
 * O `calibrated` é jsonb livre: o servidor guarda o que a tela mandou, sem
 * validar forma. E o que a tela mandava **mudou** — até a #877 o `PreviewOut`
 * expunha `config_hh` e mais nenhuma das cinco fases. Toda calibragem
 * publicada antes disso tem `numbers` sem `explore_hh`, `test_hh`, `deploy_hh`
 * e `hypercare_hh`.
 *
 * Tratar a chave ausente como zero poria `0` no **denominador da partição** e
 * a tela afirmaria ao cliente que Configurar é 100% do esforço e que as outras
 * quatro fases custam zero hora. Seria a medição inventada que este módulo
 * inteiro existe para não cometer.
 */
/**
 * A linha de Digital Access cujo consumo cabe na franquia dos usuários
 * licenciados: zero bloco a comprar, e por isso sem preço.
 *
 * Sem preço não quer dizer "a cotar" aqui. Na AL5 (16/10/2026) 79.065
 * documentos ponderados contra 1.140.000 de franquia saíam como "A cotar", e a
 * tela dizia ao cliente que havia um custo de Digital Access por vir.
 */
export function includedInAllowance(line: LicenseLine): boolean {
  return (
    line.isDigitalAccess &&
    line.entitlement !== undefined &&
    line.entitlement > 0 &&
    line.totalWeighted !== undefined &&
    line.totalWeighted <= line.entitlement &&
    (line.effectiveCount ?? 0) === 0
  );
}

/**
 * Quantas contas tem o plano de contas do cliente, pela análise vigente do
 * plano de contas no bloco `validation` (#1374). `undefined` sem análise.
 */
export function clientChartAccounts(
  validation: DiagnosticSnapshot["validation"] | undefined,
): number | undefined {
  const envelope = (validation as Record<string, unknown> | undefined)
    ?.chart_of_accounts as
    { state?: string; data?: Record<string, unknown> } | undefined;
  if (envelope?.state !== "ok") return undefined;
  const value = finiteOrNull(envelope.data?.accounts);
  return value !== null && value > 0 ? value : undefined;
}

export function projectTimeline(
  calibration: DiagnosticSnapshot["calibration"] | undefined,
): ProjectTimeline | null {
  // `null` é reservado para "NÃO HOUVE calibragem". Uma calibragem publicada
  // que não trouxe número utilizável é outra coisa, e devolver `null` aqui
  // punha na tela a contradição que esta entrega existe para impedir: o selo
  // dizendo "Calibrado por Fulano" e, três linhas abaixo, "ainda não
  // calibrado". Alcançável de dois jeitos, os dois medidos: publicar com
  // seleção vazia (a prévia devolve 200 com tudo zerado) e payload em forma
  // antiga, sem a chave `numbers`.
  if (!calibration) return null;

  const numbers = (
    calibration.calibrated as Record<string, unknown> | undefined
  )?.numbers as Record<string, unknown> | undefined;
  if (!numbers) {
    return {
      months: null,
      phases: null,
      totalHours: null,
      missing: "duration",
    };
  }

  const duration = finiteOrNull(numbers.duration_months);
  const total = finiteOrNull(numbers.total_hh);
  const hours = TIMELINE_PHASES.map((key) => finiteOrNull(numbers[key]));
  const phaseSum = hours.reduce((sum: number, value) => sum + (value ?? 0), 0);

  // Prazo zero não é prazo. O motor devolve `0.0` quando a equipe é zero (ele
  // trata a divisão com um `else 0.0`), e "0.0 meses" ao lado de uma partição
  // completa tem cara de medido. Duração ausente e duração zero são a mesma
  // resposta: não sabemos. O piso do `equipeFTE` fecha isso do outro lado.
  const months = duration !== null && duration > 0 ? duration : null;

  // Publicada, mas sem nada utilizável: ver acima, é ausência de número e não
  // ausência de calibragem.
  if (months === null && !phaseSum) {
    return {
      months: null,
      phases: null,
      totalHours: total,
      missing: "duration",
    };
  }
  // Sem duração o card não tem o número que ele existe para mostrar.
  if (months === null)
    return {
      months: null,
      phases: null,
      totalHours: total,
      missing: "duration",
    };

  // As fases vêm antes do total de propósito: o payload anterior à #877 não
  // tem nenhum dos dois, e dizer "faltam as fases" é o diagnóstico útil.
  if (hours.some((value) => value === null))
    return { months, phases: null, totalHours: total, missing: "phases" };
  const incomplete = {
    months,
    phases: null,
    totalHours: total,
    missing: "total" as const,
  };

  // O `subtotal_phases` vem do mesmo motor. Divergir quer dizer que a soma das
  // cinco não é o subtotal que o resto da conta usou.
  const subtotal = finiteOrNull(numbers.subtotal_phases);
  if (
    subtotal !== null &&
    Math.abs(phaseSum - subtotal) > SUBTOTAL_TOLERANCE_HH
  ) {
    // Motivo próprio: o total ESTÁ lá, o que não fecha é a soma das fases
    // contra o subtotal do motor. Rotular isto como "falta o total" mandaria
    // procurar um campo que existe.
    return { months, phases: null, totalHours: total, missing: "inconsistent" };
  }

  // Sem o total não dá para repartir honestamente: repartir só entre as fases
  // faria cada uma parecer o dobro do que é.
  if (
    total === null ||
    total < phaseSum - SUBTOTAL_TOLERANCE_HH ||
    total <= 0
  ) {
    return incomplete;
  }

  const overhead = Math.max(0, total - phaseSum);
  return {
    months,
    totalHours: total,
    missing: null,
    phases: [
      ...TIMELINE_PHASES.map((key, index) => ({
        key,
        hours: hours[index] as number,
        share: (hours[index] as number) / total,
      })),
      { key: TIMELINE_OVERHEAD, hours: overhead, share: overhead / total },
    ],
  };
}

/** O número, ou `null` quando a chave falta ou não é número.
 *
 * `Number(undefined)` é `NaN` e `Number(null)` é `0`: os dois esconderiam
 * ausência atrás de um valor. O `calibrated` é jsonb livre, então string e
 * ausência são ambas alcançáveis.
 */
function finiteOrNull(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}
